// @ts-nocheck
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { sendChatMessage, bookMeeting, createChatStream, getCalendarBookings, submitSupportStep } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faUser,
  faRobot,
  faCalendarAlt,
  faTimes,
  faExpand,
  faCompress,
  faBars,
  faTrash,
  faImage,
  faPaperclip,
  faExpand as faExpandAlt
} from '@fortawesome/free-solid-svg-icons';
import BotIcon from './chat/BotIcon';
import BeautifulInputField from './chat/BeautifulInputField';
import PoweredByFooter from './chat/PoweredByFooter';
import StandardMessageDisplay from './chat/StandardMessageDisplay';
import { getTierFeatures } from '../config/tierFeatures';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import 'react-calendar/dist/Calendar.css';
import './ChatInterface.css';
import { Message, SupportStepPayload, SupportSummaryPayload, SupportStepDefinition } from '../types/chat';
import ReactMarkdown from 'react-markdown';
import { CircularProgress } from '@mui/material';
import BookingForm from './calendar/BookingForm';
import { chatHistoryService } from '../services/chatHistoryService';
import feedbackService from '../services/feedbackService';
import BookingConfirmation from './calendar/BookingConfirmation';
import { CalendarView } from './calendar/CalendarView';
import CitedMessageDisplay from './chat/CitedMessageDisplay';
import MessageResponseDisplay from './chat/MessageResponseDisplay';
import CitationTooltip from './chat/CitationTooltip';
import MessageRenderer from './chat/MessageRenderer';
import { renderMarkdownContent as renderMarkdownContentCompat } from '../utils/markdownRenderer';
import { useCitationTooltip } from './chat/useCitationTooltip';
import CitationDrawer from './chat/CitationDrawer';
import { useMobileCitations } from './chat/useMobileCitations';
import moment from 'moment';
import { BookingInfo } from './calendar/types';
import ChatMessage from './ChatMessage';
import { API_BASE_URL } from '../config';
import { BookedSlotsData } from '../components/calendar/types';
import { Tooltip } from 'react-tooltip';
import MessageDisplay from './chat/MessageDisplay';
import DOMPurify from 'dompurify';
import ProfessionalCitedMessage from './chat/ProfessionalCitedMessage';
import { flushSync } from 'react-dom';
import { getClientTheme, getClientProfile } from '../services/api';
import { Theme } from '../types/theme';
import { generateUUID } from '../utils/uuid';
import SupportSessionMessage from './support/SupportSessionMessage';

interface ChatInterfaceProps {
    clientId?: string;
    isVisible?: boolean;
    onClose?: () => void;
    isWidgetMode?: boolean;
    prefetchedTheme?: Theme | null;
    initialMessage?: {
        content: string;
        role: string;
        isBot: boolean;
        id: string;
    };
}

interface EventType {
    id: number;
    title: string;
    length: number;
}

interface Slot {
    start: string;
    end: string;
}

interface BookingResponse {
    type: string;
    timezone: string;
    events: any[];
    text: string;
}

// Regex to find markdown images (can be defined globally or imported)
const markdownImageRegex = /\!\[([^\]]*)\]\(([^\)]+)\)/;

// A message is the leftover "I've cleared your chat history…" system bubble
// if it was tagged as a clear-history system response, OR if its content matches
// that wording. We use the wording fallback because older messages may have
// been saved before we started tagging metadata.
const isClearHistorySystemMessage = (msg: any): boolean => {
    if (!msg) return false;
    const meta = msg.metadata || {};
    if (meta.clear_local_storage === true) return true;
    const content = (msg.content || msg.text || '').toString().toLowerCase();
    if (meta.intent === 'system' && content.includes('cleared your chat history')) return true;
    if (content.includes("i've cleared your chat history")) return true;
    return false;
};

// A message that must never be persisted to (or reloaded from) localStorage:
// the clear-history system bubble, plus resume-failure placeholders. These used
// to get re-saved and then re-created on every reload (the resume logic finds an
// incomplete stored message, fails, writes "Could not resume…", which is saved
// again), so they appeared to "come back" even after the user cleared history.
const isUnpersistableMessage = (msg: any): boolean => {
    if (!msg) return false;
    if (isClearHistorySystemMessage(msg)) return true;
    const content = (msg.content || msg.text || '').toString().toLowerCase().trim();
    if (content === 'could not resume message - missing original query.') return true;
    if (content === 'stream could not be resumed. please try sending your message again.') return true;
    // An empty, errored/streaming bot bubble left over from an interrupted stream.
    if (msg.isBot && !content && (msg.isError || msg.isStreaming || msg.isLoading)) return true;
    return false;
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
    clientId: propClientId,
    isVisible = true,
    onClose,
    isWidgetMode,
    prefetchedTheme,
    initialMessage
}) => {
    const [clientId, setClientId] = useState<string | null>(propClientId || null);
    // Initialize messages synchronously from saved history so existing chat history
    // shows immediately on open, instead of starting empty and briefly showing a
    // welcome before the async history-load effect runs. Unpersistable bubbles
    // (clear-history/resume-failure placeholders) are filtered out.
    const [messages, setMessages] = useState<Message[]>(() => {
        try {
            if (propClientId) {
                const saved = (chatHistoryService.getHistory(propClientId) || [])
                    .filter((m: any) => !isUnpersistableMessage(m));
                if (saved.length > 0) {
                    return saved;
                }
            }
        } catch (e) {
            console.warn('[ChatInterface] Failed to load initial history:', e);
        }
        return [];
    });
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [selectedSlot, setSelectedSlot] = useState<{
        start: string;
        end: string;
        event: any;
    } | null>(null);
    const [selectedEventType, setSelectedEventType] = useState<EventType | null>(null);
    const [bookingData, setBookingData] = useState(null);
    const [userEmail, setUserEmail] = useState('');
    const [bookingInProgress, setBookingInProgress] = useState(false);
    const [bookingDetails, setBookingDetails] = useState<{
        name?: string;
        email?: string;
        notes?: string;
        bookingInfo?: BookingInfo;
    }>({});
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [calendarData, setCalendarData] = useState({
        eventTypes: [],
        selectedEventType: null,
        availableSlots: []
    });
    const [displayedConfirmations, setDisplayedConfirmations] = useState<Set<number>>(new Set());
    const [displayInCalTimezone, setDisplayInCalTimezone] = useState<boolean>(true);
    const [showBookingForm, setShowBookingForm] = useState(false);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [bookedSlots, setBookedSlots] = useState<BookedSlotsData | null>(null);
    const [userTimezone, setUserTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
    
    // Add state for tracking slot refreshes
    const [slotsNeedRefresh, setSlotsNeedRefresh] = useState<boolean>(false);
    const [lastActiveDate, setLastActiveDate] = useState<Date | null>(null);
    const [lastActiveEvent, setLastActiveEvent] = useState<any>(null);
    
    // Add a calendar refresh counter to force re-renders of all calendar instances
    const [calendarRefreshCounter, setCalendarRefreshCounter] = useState<number>(0);
    const [forceRefreshAllCalendars, setForceRefreshAllCalendars] = useState<boolean>(false);

    // Add a new state variable to track if this is the initial load
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

    // Use mobile-optimized citation system
    const { citationDrawer, handleCitationSelect, closeCitationDrawer, isMobile } = useMobileCitations(messages);
    
    // State to trigger citation tooltip re-initialization when DOM changes
    const [citationReInit, setCitationReInit] = useState(0);
    
    // Fallback to tooltip for desktop compatibility  
    const { citationTooltip, tooltipEventHandlers } = useCitationTooltip(messages, citationReInit);

    // Add a loading state to track when we're already fetching bookings
    const [isLoadingBookedSlots, setIsLoadingBookedSlots] = useState<boolean>(false);
    // Add a timestamp to track when bookings were last loaded
    const [bookedSlotsLoadedAt, setBookedSlotsLoadedAt] = useState<number | null>(null);

    const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
    // Initialize theme immediately with prefetchedTheme if available (widget mode)
    const [theme, setTheme] = useState<Theme | null>(prefetchedTheme || null);
    // Don't show loading state in widget mode since theme is prefetched
    const [isThemeLoading, setIsThemeLoading] = useState<boolean>(!isWidgetMode);
    const [showStdMenu, setShowStdMenu] = useState<boolean>(false);
    const [isStdFullscreen, setIsStdFullscreen] = useState<boolean>(false);
    const [conversationId, setConversationId] = useState<string>(generateUUID());
    const [activeSupportSession, setActiveSupportSession] = useState<string | null>(null);
    const [supportSubmitting, setSupportSubmitting] = useState<boolean>(false);
    const [imageUploadEnabled, setImageUploadEnabled] = useState<boolean>(false);
    const [currentImageStep, setCurrentImageStep] = useState<any>(null);
    const [uploadedImages, setUploadedImages] = useState<File[]>([]);

    // Debug: Log state changes
    useEffect(() => {
        console.log('[IMAGE-UPLOAD-DEBUG] imageUploadEnabled changed to:', imageUploadEnabled);
        console.log('[IMAGE-UPLOAD-DEBUG] currentImageStep:', currentImageStep);
    }, [imageUploadEnabled, currentImageStep]);

    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const activeSupportSessionRef = useRef<string | null>(null);
    
    // Add MutationObserver to detect tab changes and re-initialize citation tooltips
    useEffect(() => {
        if (!messagesContainerRef.current) return;
        
        const observer = new MutationObserver((mutations) => {
            // Check if any mutations affected the tab content
            const hasTabContentChange = mutations.some(mutation => {
                // Look for changes in tab-content or response-content elements
                return Array.from(mutation.addedNodes).some(node => 
                    node.nodeType === Node.ELEMENT_NODE && 
                    (node as Element).classList.contains('response-content')
                ) || Array.from(mutation.removedNodes).some(node => 
                    node.nodeType === Node.ELEMENT_NODE && 
                    (node as Element).classList.contains('response-content')
                );
            });
            
            if (hasTabContentChange) {
                // Trigger citation tooltip re-initialization
                setCitationReInit(prev => prev + 1);
            }
        });
        
        observer.observe(messagesContainerRef.current, {
            childList: true,
            subtree: true
        });
        
        return () => observer.disconnect();
    }, []);

    // Add new state variables for streaming
const [streamAbortController, setStreamAbortController] = useState<(() => void) | null>(null);
const streamAbortRef = useRef<(() => void) | null>(null);
const [lastScrollPosition, setLastScrollPosition] = useState(0);

// Add a separate ref for the streaming message
const streamingMessageRef = useRef<HTMLDivElement>(null);
    
    // Add a new ref to track which booking messages we've already processed
    const processedBookingMessagesRef = useRef(new Set());
    
    // Add a flag to prevent simultaneous calendar refreshes
const calendarRefreshingRef = useRef<boolean>(false);

// Use a more robust way to track the last rendered message
const lastRenderedMessageIndexRef = useRef<number>(-1);

    useEffect(() => {
        activeSupportSessionRef.current = activeSupportSession;
    }, [activeSupportSession]);

    // Cross-tab clear sync: if chat history is cleared in ANOTHER tab, reset this
    // tab's UI. We intentionally do NOT run a same-tab "messages vs localStorage"
    // heuristic here. That heuristic raced with normal sending: when the user asked
    // the first question right after clearing, messages.length went 1 -> 2 and this
    // effect re-ran and overwrote the question/answer with "Chat history has been
    // cleared." before the save effect had a chance to persist the new conversation.
    // The clear button already resets the UI via the clear_local_storage response
    // handler, so this listener only needs to cover the cross-tab case (storage
    // events do not fire in the tab that made the change).
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'storage' || event.key === null) {
                if (!clientId) return;
                const histories = chatHistoryService.getAllHistories();
                const clientHistory = histories.find(h => h.clientId === clientId);
                if (!clientHistory) {
                    console.log('[ChatInterface] Cross-tab clear detected, resetting UI');
                    setMessages([{
                        id: `system-${Date.now()}`,
                        content: "Chat history has been cleared.",
                        isBot: true,
                        role: 'assistant',
                        isHtml: false
                    }]);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [clientId]);

    // Auto-scroll management with user interruption detection
    const autoScrollEnabledRef = useRef(true);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Set while WE scroll programmatically, so the scroll listener doesn't mistake
    // our own scroll for a user scrolling up and disable auto-scroll.
    const programmaticScrollRef = useRef(false);

    // Define the scrollToBottom function. `force` bypasses the auto-scroll flag
    // (used when a new question/turn arrives — it must always come into view).
    const scrollToBottom = (behavior: 'smooth' | 'auto' | 'instant' = 'smooth', force = false) => {
        if (!force && !autoScrollEnabledRef.current) return;
        // Mark this as a programmatic scroll so the scroll listener doesn't disable
        // auto-scroll on the intermediate scroll events we're about to generate.
        programmaticScrollRef.current = true;
        const container = messagesContainerRef.current as HTMLElement | null;
        if (container) {
            // Scroll the chat container directly — most reliable inside the widget
            // (scrollIntoView can target the wrong ancestor when embedded).
            container.scrollTo({ top: container.scrollHeight, behavior: behavior as ScrollBehavior });
        } else if (messagesEndRef.current) {
            (messagesEndRef.current as HTMLElement).scrollIntoView({
                behavior: behavior as ScrollBehavior,
                block: 'end'
            });
        }
        // Clear the programmatic flag after the scroll settles.
        setTimeout(() => { programmaticScrollRef.current = false; }, behavior === 'smooth' ? 400 : 80);
    };

    // Add scroll effect for new messages (smooth scroll)
    useEffect(() => {
        // A new message/turn arrived (e.g. the next screening question). Always
        // bring it into view — re-enable auto-scroll even if the user had scrolled
        // up during the previous turn, so every newly asked question is visible.
        if (messages.length > 0) {
            autoScrollEnabledRef.current = true;
            // Force an instant scroll (bypasses the auto-scroll flag and avoids
            // smooth-animation scroll events that would re-disable auto-scroll).
            requestAnimationFrame(() => {
                scrollToBottom('auto', true);
                // Second pass after layout settles (streamed content grows height).
                setTimeout(() => scrollToBottom('auto', true), 150);
            });
        }
    }, [messages.length]);

    // Separate effect for streaming content updates (instant scroll for smooth experience)
    useEffect(() => {
        // Check if the last message is currently streaming
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.isBot && lastMessage.isStreaming) {
            // Use requestAnimationFrame for smooth, non-blocking scroll
            requestAnimationFrame(() => {
                scrollToBottom('auto'); // Use auto for immediate, no-animation scroll during streaming
            });
        }
    }, [messages]); // Re-run whenever messages array updates

    // Track user scroll position to toggle auto-scroll (with debounce for better UX)
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const onScroll = () => {
            // Ignore scroll events caused by our own programmatic scrolling — only
            // a real user scrolling up should disable auto-scroll.
            if (programmaticScrollRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = container;
            // Consider "at bottom" if within 50px of bottom (more forgiving threshold)
            const atBottom = scrollHeight - (scrollTop + clientHeight) < 50;

            // If user scrolls up significantly, disable auto-scroll
            if (!atBottom) {
                autoScrollEnabledRef.current = false;
            } else {
                // Re-enable auto-scroll when user scrolls back to bottom
                autoScrollEnabledRef.current = true;
                // Clear any pending timeout
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
            }
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            container.removeEventListener('scroll', onScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputMessage(e.target.value);
    };

    // For Standard: replace citation markers with inline source links
    const renderStandardHTML = (msg: any, idx: number) => {
        const base = renderMarkdownContentCompat(msg.content || '');
        let rawCitations: any = msg?.metadata?.citations || msg?.citation_sources || msg?.metadata?.citation_sources || [];
        if (rawCitations && !Array.isArray(rawCitations) && typeof rawCitations === 'object') rawCitations = Object.values(rawCitations);
        const citations = (rawCitations || []).filter((c: any) => c && typeof c === 'object');
        const linkColor = (theme as any)?.standardLinkColor || '#2563EB';
        const iconColor = linkColor;
        const escapeHtml = (str: string) => (str || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
        const getLabel = (c: any): string => {
            const raw = c?.title || c?.source_title || c?.page_title || c?.name || '';
            const cleaned = (raw || '').toString().trim();
            if (cleaned.length > 0) return cleaned;
            try {
                const url = new URL(c?.url || '');
                return url.hostname.replace('www.', '');
            } catch {
                return 'Source';
            }
        };
        const replacer = (match: string, numStr: string) => {
            const n = parseInt(numStr, 10) - 1;
            const c = citations[n];
            if (!c) return ''; // Invalid citation reference
            const fullLabel = getLabel(c);
            const label = escapeHtml(fullLabel);
            // If no URL (document/PDF), show as non-clickable span with document icon
            if (!c.url) {
                return `<span class=\"std-inline-source std-inline-source-doc\" title=\"${label}\" style=\"cursor: default;\"><svg width=\"12\" height=\"12\" viewBox=\"0 0 24 24\" fill=\"none\"><path d=\"M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z\" stroke=\"${iconColor}\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/><polyline points=\"14 2 14 8 20 8\" stroke=\"${iconColor}\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg>${label}</span>`;
            }
            return `<a class=\"std-inline-source\" href=\"${c.url}\" target=\"_blank\" rel=\"noopener noreferrer\"><svg width=\"12\" height=\"12\" viewBox=\"0 0 24 24\" fill=\"none\"><path d=\"M10 14L21 3M21 3h-7m7 0v7\" stroke=\"${iconColor}\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg>${label}</a>`;
        };
        // Replace span citation markers first
        let html = base.replace(/<span[^>]*class=\"citation-marker\"[^>]*data-source-number=\"(\d+)\"[^>]*>.*?<\/span>/g, replacer);
        // Also handle raw [n] patterns if present
        html = html.replace(/\[(\d+)\]/g, replacer);
        return html;
    };

    // Add handler for keyDown to process Enter and Shift+Enter
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            // If Shift key is pressed, allow default behavior (new line)
            if (e.shiftKey) {
                return;
            }

            // Otherwise, prevent default (new line) and submit the message
            e.preventDefault();

            // Only submit if not loading and there's a message or uploaded images
            if (!isLoading) {
                // If there are uploaded images, send them with the message
                if (uploadedImages.length > 0) {
                    handleSendImages();
                } else if (inputMessage.trim()) {
                    handleSubmit(e);
                }
            }
        }
    };

    useEffect(() => {
        const initClientId = () => {
            if (propClientId) {
                setClientId(propClientId);

                // In widget mode with prefetched theme, skip theme fetch
                if (isWidgetMode && prefetchedTheme) {
                    console.log('Widget mode: Using prefetched theme from Widget component');
                    setTheme(prefetchedTheme);
                    setIsThemeLoading(false);
                    return;
                }

                // Load theme and profile for the client
                Promise.all([
                    getClientTheme(propClientId),
                    getClientProfile(propClientId).catch(err => {
                        console.warn('Profile loading failed, using defaults:', err);
                        return null; // Return null instead of throwing
                    })
                ])
                    .then(([themeData, profile]) => {
                        if (themeData) {
                            console.log('ChatInterface loaded theme data for client:', propClientId, {
                                primaryColor: themeData.primaryColor,
                                secondaryColor: themeData.secondaryColor,
                                buttonBgColor: themeData.buttonBgColor,
                                chatSendButtonBg: themeData.chatSendButtonBg
                            });
                            
                            // Merge with defaults for chat interface
                            const mergedTheme = {
                                ...themeData,
                                // Include basic theme properties that might be missing
                                primaryColor: themeData.primaryColor || '#667eea',
                                secondaryColor: themeData.secondaryColor || '#333332',
                                buttonBgColor: themeData.buttonBgColor || '#667eea',
                                buttonTextColor: themeData.buttonTextColor || '#ffffff',
                                chatContainerUseGradient: themeData.chatContainerUseGradient ?? 'true',
                                chatContainerGradientStart: themeData.chatContainerGradientStart || '#1e293b',
                                chatContainerGradientEnd: themeData.chatContainerGradientEnd || '#334155',
                                chatContainerOpacity: themeData.chatContainerOpacity ?? 95,
                                chatContainerBg: themeData.chatContainerBg || '#374151',
                                chatSendIconColor: themeData.chatSendIconColor || '#ffffff',
                                chatSendButtonBg: themeData.chatSendButtonBg || themeData.primaryColor || themeData.buttonBgColor || '#667eea',
                                chatTextColor: themeData.chatTextColor || '#ffffff',
                                chatCitationTooltipBg: themeData.chatCitationTooltipBg || '#1e293b',
                                chatCitationTooltipText: themeData.chatCitationTooltipText || '#ffffff',
                                chatCloseIconColor: themeData.chatCloseIconColor || '#ffffff',
                                chatCloseIconBg: themeData.chatCloseIconBg || '#374151',
                                chatMessageResponseBg: themeData.chatMessageResponseBg || '#1e293b',
                                // Clear button defaults
                                chatClearButtonShow: themeData.chatClearButtonShow || 'true',
                                chatClearButtonPosition: themeData.chatClearButtonPosition || 'header-right',
                                chatClearButtonBg: themeData.chatClearButtonBg || '#374151',
                                chatClearButtonColor: themeData.chatClearButtonColor || '#ffffff',
                                chatClearButtonHoverBg: themeData.chatClearButtonHoverBg || '#4b5563',
                                chatClearButtonHoverColor: themeData.chatClearButtonHoverColor || '#ffffff',
                                chatClearButtonText: themeData.chatClearButtonText || 'Clear Chat',
                                // Sources tab customization
                                chatSourcesTabBorderColor: themeData.chatSourcesTabBorderColor || '#3b82f6',
                                chatSourcesTabTextColor: themeData.chatSourcesTabTextColor || '#ffffff',
                                chatSourcesTabActiveTextColor: themeData.chatSourcesTabActiveTextColor || '#3b82f6',
                                chatSourcesItemBorderColor: (themeData.chatSourcesItemBorderColor && themeData.chatSourcesItemBorderColor !== '') ? themeData.chatSourcesItemBorderColor : '#1e1e1e',
                                chatSourcesItemBg: (themeData.chatSourcesItemBg && themeData.chatSourcesItemBg !== '') ? themeData.chatSourcesItemBg : '#1e1e1e',
                                chatSourcesItemHeaderBg: (themeData.chatSourcesItemHeaderBg && themeData.chatSourcesItemHeaderBg !== '') ? themeData.chatSourcesItemHeaderBg : '#1e1e1e',
                                chatSourcesLinkColor: themeData.chatSourcesLinkColor || '#60a5fa',
                                chatHyperlinkColor: themeData.chatHyperlinkColor || '#60a5fa',
                                chatSourcesScrollBg: themeData.chatSourcesScrollBg || '#1e293b',
                                chatSourcesScrollBorderColor: themeData.chatSourcesScrollBorderColor || '#475569',
                                chatSourcesTextColor: themeData.chatSourcesTextColor || '#e5e7eb',
                                chatSourcesMarkerBg: themeData.chatSourcesMarkerBg || '#3b82f6',
                                chatSourcesMarkerColor: themeData.chatSourcesMarkerColor || '#ffffff',
                                chatSourcesTabMarkerBg: themeData.chatSourcesTabMarkerBg || '#10b981',
                                chatSourcesTabMarkerColor: themeData.chatSourcesTabMarkerColor || '#ffffff',
                                // Feedback icons
                                chatFeedbackIconColor: themeData.chatFeedbackIconColor || '#9ca3af',
                                chatFeedbackIconHoverColor: themeData.chatFeedbackIconHoverColor || '#6b7280',
                                chatFeedbackActiveColor: themeData.chatFeedbackActiveColor || themeData.primaryColor || '#3b82f6',
                                chatFeedbackBg: themeData.chatFeedbackBg || 'transparent',
                                chatFeedbackHoverBg: themeData.chatFeedbackHoverBg || 'rgba(107, 114, 128, 0.1)',
                                chatFeedbackActiveBg: themeData.chatFeedbackActiveBg || 'rgba(59, 130, 246, 0.1)',
                                chatFeedbackBorderColor: themeData.chatFeedbackBorderColor || 'transparent',
                                chatFeedbackHoverBorderColor: themeData.chatFeedbackHoverBorderColor || '#e5e7eb',
                                chatFeedbackActiveBorderColor: themeData.chatFeedbackActiveBorderColor || '#3b82f6',
                                // Feedback tags
                                chatFeedbackTagBg: themeData.chatFeedbackTagBg || '#364356',
                                chatFeedbackTagColor: themeData.chatFeedbackTagColor || '#ffffff',
                                chatFeedbackTagBorderColor: themeData.chatFeedbackTagBorderColor || '#475569',
                                chatFeedbackTagHoverBg: themeData.chatFeedbackTagHoverBg || '#4a5568',
                                chatFeedbackTagHoverBorderColor: themeData.chatFeedbackTagHoverBorderColor || '#718096',
                                chatFeedbackTagActiveBg: themeData.chatFeedbackTagActiveBg || '#2d3748',
                                chatFeedbackTagLabelColor: themeData.chatFeedbackTagLabelColor || '#ffffff',
                                chatFeedbackThankYouColor: themeData.chatFeedbackThankYouColor || '#ffffff',
                                // View Source button
                                chatViewSourceBg: themeData.chatViewSourceBg || 'rgba(59, 130, 246, 0.1)',
                                chatViewSourceColor: themeData.chatViewSourceColor || '#60a5fa',
                                chatViewSourceHoverBg: themeData.chatViewSourceHoverBg || 'rgba(59, 130, 246, 0.2)',
                                chatViewSourceHoverColor: themeData.chatViewSourceHoverColor || '#93c5fd',
                                // Font sizes - ensure they are valid numbers within reasonable range
                                chatFontSizeBase: Math.max(14, Math.min(24, Number(themeData.chatFontSizeBase) || 14)),
                                chatFontSizeResponse: Math.max(14, Math.min(24, Number(themeData.chatFontSizeResponse) || 14)),
                                chatFontSizeUser: Math.max(14, Math.min(24, Number(themeData.chatFontSizeUser) || 14)),
                                chatFontSizeHeader: Math.max(16, Math.min(28, Number(themeData.chatFontSizeHeader) || 16)),
                                chatFontSizePlaceholder: Math.max(14, Math.min(20, Number(themeData.chatFontSizePlaceholder) || 14)),
                                // Font family
                                fontFamily: themeData.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                            };
                            const displayName = (profile?.prompt_display_name || profile?.name || 'Bot') as string;
                            const finalTheme = { ...mergedTheme, headerTitle: displayName };
                            console.log('Setting final theme in ChatInterface:', {
                                primaryColor: finalTheme.primaryColor,
                                chatSendButtonBg: finalTheme.chatSendButtonBg,
                                buttonBgColor: finalTheme.buttonBgColor,
                                chatContainerGradientStart: finalTheme.chatContainerGradientStart,
                                chatContainerGradientEnd: finalTheme.chatContainerGradientEnd
                            });
                            setTheme(finalTheme);
                        }
                        setIsThemeLoading(false);
                    })
                    .catch(err => {
                        console.error('Error loading theme:', err);
                        setIsThemeLoading(false);
                    });
                return;
            }

            const storedClientId = localStorage.getItem('client_id');
            if (storedClientId) {
                setClientId(storedClientId);
                // Load theme and profile for the client
                Promise.all([
                    getClientTheme(storedClientId),
                    getClientProfile(storedClientId).catch(err => {
                        console.warn('Profile loading failed, using defaults:', err);
                        return null; // Return null instead of throwing
                    })
                ])
                    .then(([themeData, profile]) => {
                        if (themeData) {
                            console.log('ChatInterface loaded theme data for client:', propClientId, {
                                primaryColor: themeData.primaryColor,
                                secondaryColor: themeData.secondaryColor,
                                buttonBgColor: themeData.buttonBgColor,
                                chatSendButtonBg: themeData.chatSendButtonBg
                            });
                            
                            // Merge with defaults for chat interface
                            const mergedTheme = {
                                ...themeData,
                                // Include basic theme properties that might be missing
                                primaryColor: themeData.primaryColor || '#667eea',
                                secondaryColor: themeData.secondaryColor || '#333332',
                                buttonBgColor: themeData.buttonBgColor || '#667eea',
                                buttonTextColor: themeData.buttonTextColor || '#ffffff',
                                chatContainerUseGradient: themeData.chatContainerUseGradient ?? 'true',
                                chatContainerGradientStart: themeData.chatContainerGradientStart || '#1e293b',
                                chatContainerGradientEnd: themeData.chatContainerGradientEnd || '#334155',
                                chatContainerOpacity: themeData.chatContainerOpacity ?? 95,
                                chatContainerBg: themeData.chatContainerBg || '#374151',
                                chatSendIconColor: themeData.chatSendIconColor || '#ffffff',
                                chatSendButtonBg: themeData.chatSendButtonBg || themeData.primaryColor || themeData.buttonBgColor || '#667eea',
                                chatTextColor: themeData.chatTextColor || '#ffffff',
                                chatCitationTooltipBg: themeData.chatCitationTooltipBg || '#1e293b',
                                chatCitationTooltipText: themeData.chatCitationTooltipText || '#ffffff',
                                chatCloseIconColor: themeData.chatCloseIconColor || '#ffffff',
                                chatCloseIconBg: themeData.chatCloseIconBg || '#374151',
                                chatMessageResponseBg: themeData.chatMessageResponseBg || '#1e293b',
                                // Clear button defaults
                                chatClearButtonShow: themeData.chatClearButtonShow || 'true',
                                chatClearButtonPosition: themeData.chatClearButtonPosition || 'header-right',
                                chatClearButtonBg: themeData.chatClearButtonBg || '#374151',
                                chatClearButtonColor: themeData.chatClearButtonColor || '#ffffff',
                                chatClearButtonHoverBg: themeData.chatClearButtonHoverBg || '#4b5563',
                                chatClearButtonHoverColor: themeData.chatClearButtonHoverColor || '#ffffff',
                                chatClearButtonText: themeData.chatClearButtonText || 'Clear Chat',
                                // Sources tab customization
                                chatSourcesTabBorderColor: themeData.chatSourcesTabBorderColor || '#3b82f6',
                                chatSourcesTabTextColor: themeData.chatSourcesTabTextColor || '#ffffff',
                                chatSourcesTabActiveTextColor: themeData.chatSourcesTabActiveTextColor || '#3b82f6',
                                chatSourcesItemBorderColor: (themeData.chatSourcesItemBorderColor && themeData.chatSourcesItemBorderColor !== '') ? themeData.chatSourcesItemBorderColor : '#1e1e1e',
                                chatSourcesItemBg: (themeData.chatSourcesItemBg && themeData.chatSourcesItemBg !== '') ? themeData.chatSourcesItemBg : '#1e1e1e',
                                chatSourcesItemHeaderBg: (themeData.chatSourcesItemHeaderBg && themeData.chatSourcesItemHeaderBg !== '') ? themeData.chatSourcesItemHeaderBg : '#1e1e1e',
                                chatSourcesLinkColor: themeData.chatSourcesLinkColor || '#60a5fa',
                                chatHyperlinkColor: themeData.chatHyperlinkColor || '#60a5fa',
                                chatSourcesScrollBg: themeData.chatSourcesScrollBg || '#1e293b',
                                chatSourcesScrollBorderColor: themeData.chatSourcesScrollBorderColor || '#475569',
                                chatSourcesTextColor: themeData.chatSourcesTextColor || '#e5e7eb',
                                chatSourcesMarkerBg: themeData.chatSourcesMarkerBg || '#3b82f6',
                                chatSourcesMarkerColor: themeData.chatSourcesMarkerColor || '#ffffff',
                                chatSourcesTabMarkerBg: themeData.chatSourcesTabMarkerBg || '#10b981',
                                chatSourcesTabMarkerColor: themeData.chatSourcesTabMarkerColor || '#ffffff',
                                // Feedback icons
                                chatFeedbackIconColor: themeData.chatFeedbackIconColor || '#9ca3af',
                                chatFeedbackIconHoverColor: themeData.chatFeedbackIconHoverColor || '#6b7280',
                                chatFeedbackActiveColor: themeData.chatFeedbackActiveColor || themeData.primaryColor || '#3b82f6',
                                chatFeedbackBg: themeData.chatFeedbackBg || 'transparent',
                                chatFeedbackHoverBg: themeData.chatFeedbackHoverBg || 'rgba(107, 114, 128, 0.1)',
                                chatFeedbackActiveBg: themeData.chatFeedbackActiveBg || 'rgba(59, 130, 246, 0.1)',
                                chatFeedbackBorderColor: themeData.chatFeedbackBorderColor || 'transparent',
                                chatFeedbackHoverBorderColor: themeData.chatFeedbackHoverBorderColor || '#e5e7eb',
                                chatFeedbackActiveBorderColor: themeData.chatFeedbackActiveBorderColor || '#3b82f6',
                                // Feedback tags
                                chatFeedbackTagBg: themeData.chatFeedbackTagBg || '#364356',
                                chatFeedbackTagColor: themeData.chatFeedbackTagColor || '#ffffff',
                                chatFeedbackTagBorderColor: themeData.chatFeedbackTagBorderColor || '#475569',
                                chatFeedbackTagHoverBg: themeData.chatFeedbackTagHoverBg || '#4a5568',
                                chatFeedbackTagHoverBorderColor: themeData.chatFeedbackTagHoverBorderColor || '#718096',
                                chatFeedbackTagActiveBg: themeData.chatFeedbackTagActiveBg || '#2d3748',
                                chatFeedbackTagLabelColor: themeData.chatFeedbackTagLabelColor || '#ffffff',
                                chatFeedbackThankYouColor: themeData.chatFeedbackThankYouColor || '#ffffff',
                                // View Source button
                                chatViewSourceBg: themeData.chatViewSourceBg || 'rgba(59, 130, 246, 0.1)',
                                chatViewSourceColor: themeData.chatViewSourceColor || '#60a5fa',
                                chatViewSourceHoverBg: themeData.chatViewSourceHoverBg || 'rgba(59, 130, 246, 0.2)',
                                chatViewSourceHoverColor: themeData.chatViewSourceHoverColor || '#93c5fd',
                                // Font sizes - ensure they are valid numbers within reasonable range
                                chatFontSizeBase: Math.max(14, Math.min(24, Number(themeData.chatFontSizeBase) || 14)),
                                chatFontSizeResponse: Math.max(14, Math.min(24, Number(themeData.chatFontSizeResponse) || 14)),
                                chatFontSizeUser: Math.max(14, Math.min(24, Number(themeData.chatFontSizeUser) || 14)),
                                chatFontSizeHeader: Math.max(16, Math.min(28, Number(themeData.chatFontSizeHeader) || 16)),
                                chatFontSizePlaceholder: Math.max(14, Math.min(20, Number(themeData.chatFontSizePlaceholder) || 14)),
                                // Font family
                                fontFamily: themeData.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                            };
                            const displayName = (profile?.prompt_display_name || profile?.name || 'Bot') as string;
                            const finalTheme = { ...mergedTheme, headerTitle: displayName };
                            console.log('Setting final theme in ChatInterface:', {
                                primaryColor: finalTheme.primaryColor,
                                chatSendButtonBg: finalTheme.chatSendButtonBg,
                                buttonBgColor: finalTheme.buttonBgColor,
                                chatContainerGradientStart: finalTheme.chatContainerGradientStart,
                                chatContainerGradientEnd: finalTheme.chatContainerGradientEnd
                            });
                            setTheme(finalTheme);
                        }
                        setIsThemeLoading(false);
                    })
                    .catch(err => {
                        console.error('Error loading theme:', err);
                        setIsThemeLoading(false);
                    });
                return;
            }

            console.error('No client ID available');
            setIsThemeLoading(false);
        };

        initClientId();
    }, [propClientId]);

    // Auto-focus input field after bot response (for standard sidebar)
    useEffect(() => {
        if (!isLoading && (theme?.interfaceVariant === 'standard' || theme?.interfaceVariant === 'proactive') && textareaRef.current) {
            // Small delay to ensure DOM is ready after response
            setTimeout(() => {
                textareaRef.current?.focus?.();
            }, 100);
        }
    }, [isLoading, theme?.interfaceVariant]);

    useEffect(() => {
        if (!theme) {
            return;
        }

        const position = theme.interfacePosition === 'left' ? 'left' : 'right';
        const baseSidebarWidth = Number(theme.standardSidebarWidth) || 380;
        const fullscreenSidebarWidth = Number(theme.standardFullscreenWidth) || 600;
        const activeSidebarWidth = isStdFullscreen ? fullscreenSidebarWidth : baseSidebarWidth;
        const isStandardVariant = theme.interfaceVariant === 'standard' || theme.interfaceVariant === 'proactive';
        const isOpen = isStandardVariant && isVisible;

        window.dispatchEvent(new CustomEvent('ai-widget-sidebar-toggle', {
            detail: {
                isOpen,
                position,
                width: activeSidebarWidth
            }
        }));
    }, [theme, isVisible, isStdFullscreen]);

    useEffect(() => {
        if (!theme || (theme.interfaceVariant !== 'standard' && theme.interfaceVariant !== 'proactive')) {
            return;
        }

        const position = theme.interfacePosition === 'left' ? 'left' : 'right';
        const baseSidebarWidth = Number(theme.standardSidebarWidth) || 380;

        return () => {
            window.dispatchEvent(new CustomEvent('ai-widget-sidebar-toggle', {
                detail: {
                    isOpen: false,
                    position,
                    width: baseSidebarWidth
                }
            }));
        };
    }, [theme]);

    // Inject font styles into parent document for widget mode
    useEffect(() => {
        if (isWidgetMode && theme) {
            // Create style element for parent document
            const styleId = 'ai-chat-widget-font-overrides';
            let styleElement = document.getElementById(styleId) as HTMLStyleElement;
            
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = styleId;
                document.head.appendChild(styleElement);
            }
            
            // Add aggressive font size overrides
            styleElement.textContent = `
                /* AI Chat Widget Typography System - Maximum Specificity */
                html body .chat-overlay,
                html body #ai-widget-container,
                html body #ai-chat-widget-container {
                    font-family: ${theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'} !important;
                }
                
                /* Base font size for chat container - foundation */
                html body .chat-overlay .chat-container,
                html body #ai-widget-container .chat-container,
                html body #ai-chat-widget-container .chat-container {
                    font-size: ${theme?.chatFontSizeBase ? `${theme.chatFontSizeBase}px` : '14px'} !important;
                    line-height: 1.6 !important;
                }
                
                /* Response message typography */
                html body .chat-overlay .message-response-display,
                html body .chat-overlay .response-content,
                html body .chat-overlay .markdown-content,
                html body #ai-widget-container .message-response-display,
                html body #ai-widget-container .response-content,
                html body #ai-widget-container .markdown-content,
                html body #ai-chat-widget-container .message-response-display,
                html body #ai-chat-widget-container .response-content,
                html body #ai-chat-widget-container .markdown-content {
                    font-size: ${theme?.chatFontSizeResponse ? `${theme.chatFontSizeResponse}px` : '14px'} !important;
                }
                
                /* Response paragraphs */
                html body .chat-overlay .response-content p,
                html body .chat-overlay .markdown-content p,
                html body #ai-widget-container .response-content p,
                html body #ai-widget-container .markdown-content p,
                html body #ai-chat-widget-container .response-content p,
                html body #ai-chat-widget-container .markdown-content p {
                    font-size: ${theme?.chatFontSizeResponse ? `${theme.chatFontSizeResponse}px` : '14px'} !important;
                    margin: 0.8em 0;
                }
                
                /* Response headings - relative sizing */
                html body .chat-overlay .response-content h1,
                html body .chat-overlay .markdown-content h1,
                html body #ai-widget-container .response-content h1,
                html body #ai-widget-container .markdown-content h1 {
                    font-size: 1.8em !important;
                    font-weight: 600 !important;
                }
                
                html body .chat-overlay .response-content h2,
                html body .chat-overlay .markdown-content h2,
                html body #ai-widget-container .response-content h2,
                html body #ai-widget-container .markdown-content h2 {
                    font-size: 1.5em !important;
                    font-weight: 600 !important;
                }
                
                html body .chat-overlay .response-content h3,
                html body .chat-overlay .markdown-content h3,
                html body #ai-widget-container .response-content h3,
                html body #ai-widget-container .markdown-content h3 {
                    font-size: 1.2em !important;
                    font-weight: 600 !important;
                }
                
                /* User messages */
                html body .chat-overlay .message-wrapper.user,
                html body .chat-overlay .user-message,
                html body #ai-widget-container .message-wrapper.user,
                html body #ai-chat-widget-container .message-wrapper.user {
                    font-size: ${theme?.chatFontSizeUser ? `${theme.chatFontSizeUser}px` : '14px'} !important;
                }
                
                /* Chat header */
                html body .chat-overlay .chat-header,
                html body #ai-widget-container .chat-header,
                html body #ai-chat-widget-container .chat-header {
                    font-size: ${theme?.chatFontSizeHeader ? `${theme.chatFontSizeHeader}px` : '16px'} !important;
                    font-weight: 600 !important;
                }
                
                /* Input and placeholder */
                html body .chat-overlay input,
                html body .chat-overlay textarea,
                html body #ai-widget-container input,
                html body #ai-widget-container textarea,
                html body #ai-chat-widget-container input,
                html body #ai-chat-widget-container textarea {
                    font-size: ${theme?.chatFontSizePlaceholder ? `${theme.chatFontSizePlaceholder}px` : '14px'} !important;
                    font-family: ${theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'} !important;
                }
                
                html body .chat-overlay input::placeholder,
                html body .chat-overlay textarea::placeholder,
                html body #ai-widget-container input::placeholder,
                html body #ai-widget-container textarea::placeholder,
                html body #ai-chat-widget-container input::placeholder,
                html body #ai-chat-widget-container textarea::placeholder {
                    font-size: ${theme?.chatFontSizePlaceholder ? `${theme.chatFontSizePlaceholder}px` : '14px'} !important;
                    opacity: 0.7;
                }
                
                /* Lists */
                html body .chat-overlay .response-content ul,
                html body .chat-overlay .response-content ol,
                html body .chat-overlay .markdown-content ul,
                html body .chat-overlay .markdown-content ol {
                    font-size: ${theme?.chatFontSizeResponse ? `${theme.chatFontSizeResponse}px` : '14px'} !important;
                }
                
                /* Links */
                html body .chat-overlay a,
                html body #ai-widget-container a,
                html body #ai-chat-widget-container a {
                    color: ${theme?.chatHyperlinkColor || '#3b82f6'} !important;
                    font-weight: ${theme?.chatLinkFontWeight || 'normal'} !important;
                    text-decoration: ${theme?.chatLinkUnderline === 'true' ? 'underline' : 'none'} !important;
                }
                
                html body .chat-overlay a:hover,
                html body #ai-widget-container a:hover,
                html body #ai-chat-widget-container a:hover {
                    text-decoration: underline !important;
                    opacity: 0.8;
                }
            `;
            
            return () => {
                // Cleanup on unmount
                if (styleElement && styleElement.parentNode) {
                    styleElement.parentNode.removeChild(styleElement);
                }
            };
        }
    }, [isWidgetMode, theme]);

    useEffect(() => {
        // Use propClientId as the source of truth so history loads even before the
        // internal clientId state has synced (the state starts null on mount, which
        // previously delayed the load and let a welcome message show first).
        const effectiveClientId = clientId || propClientId;
        console.log('[History Load] Effect running. Client ID:', effectiveClientId, 'isInitialLoad:', isInitialLoad);
        if (effectiveClientId) {
            console.log(`[History Load] Attempting to load history for client: ${effectiveClientId}`);
            const rawHistory = chatHistoryService.getHistory(effectiveClientId);
            // Drop the orphan "I've cleared your chat history…" system bubble.
            // After a clear-history flow, that confirmation often ends up as the
            // sole message in localStorage (the message lives in state through
            // the save effect even though clearHistory() ran a tick earlier).
            // Showing it on the next open looks like a stray reply above the
            // user's first real question, so we filter it out at load time.
            const savedHistory = (rawHistory || []).filter((msg: any) => !isUnpersistableMessage(msg));
            console.log('[History Load] History retrieved from service:', savedHistory);

            if (savedHistory && savedHistory.length > 0) {
                console.log(`[History Load] Found ${savedHistory.length} messages in history. Processing...`);
                // Find the most recent calendar message for special handling
                const calendarMessages = savedHistory.filter(msg => msg.type === 'booking');
                const mostRecentCalendarIndex = calendarMessages.length > 0
                    ? savedHistory.indexOf(calendarMessages[calendarMessages.length - 1])
                    : -1;

                // Process history - only most recent calendar is visible by default
                const processedHistory = savedHistory.map((message, index) => {
                    // If it's a calendar booking message, handle visibility
                    if (message.type === 'booking') {
                        // Only show the most recent calendar by default if not in initial load
                        const shouldShowCalendar = index === mostRecentCalendarIndex && !isInitialLoad;
                        
                        return {
                            ...message,
                            showCalendar: shouldShowCalendar,
                            initiallyHidden: true // Mark all calendars as initially hidden
                        };
                    }
                    return message;
                });
                
                console.log('[History Load] Setting messages state with processed history.');
                setMessages((prev) => {
                    // Never clobber a live/in-flight conversation with the saved
                    // snapshot. If the current state is ahead of the snapshot (a
                    // message is streaming/loading, or there are more messages than
                    // the snapshot), keep it — otherwise a re-run of this effect during
                    // the first reply after reload would wipe the streaming bot bubble
                    // and the response would be lost.
                    const liveAhead = (prev || []).some((m: any) => m?.isStreaming || m?.isLoading)
                        || (prev || []).length > processedHistory.length;
                    if (liveAhead) {
                        console.log('[History Load] Live conversation ahead of snapshot — not clobbering.');
                        return prev;
                    }
                    return processedHistory;
                });
            } else {
                // No saved history. Only show the default welcome if we don't already
                // have messages in state — never clobber an in-progress conversation or
                // lazily-loaded history with a welcome bubble.
                setMessages((prev) => {
                    if (prev && prev.length > 0) {
                        console.log('[History Load] No saved history, but messages already present — keeping them.');
                        return prev;
                    }
                    console.log('[History Load] No history found. Setting default welcome message.');
                    return [
                        {
                            content: "Welcome! How can I help you today?",
                            isBot: true,
                            role: 'assistant'
                        }
                    ];
                });
            }
        } else {
            console.log('[History Load] No Client ID available yet.');
        }
    }, [clientId, propClientId, isInitialLoad]);

    useEffect(() => {
        if (clientId && messages.length > 0) {
            // Don't persist the "I've cleared your chat history…" system bubble.
            // It used to survive the clear flow because the save effect re-saved
            // it right after chatHistoryService.clearHistory() ran, leaving an
            // orphan system message at the top of the next session.
            const persistable = messages.filter((m: any) => !isUnpersistableMessage(m));
            // Never persist a lone default welcome bubble — doing so would overwrite
            // any real saved history with just the greeting (the bug where reopening
            // showed a welcome and the prior conversation appeared lost).
            const isOnlyDefaultWelcome = persistable.length === 1
                && persistable[0]?.isBot
                && !persistable[0]?.id
                && persistable[0]?.content === "Welcome! How can I help you today?";
            if (persistable.length > 0 && !isOnlyDefaultWelcome) {
                chatHistoryService.saveHistory(clientId, persistable);
            }
        }
    }, [messages, clientId]);

    // Modified useEffect to prevent infinite update loops with booking messages
    useEffect(() => {
        if (messages.length === 0) return;
    
        // Only process if the current message index is greater than the last one we processed
        const currentIndex = messages.length - 1;
        if (currentIndex <= lastRenderedMessageIndexRef.current) return;
    
        const lastMessage = messages[currentIndex];
        
        // Only process booking messages with events
        if (lastMessage?.type === 'booking' && lastMessage.events?.length > 0) {
            // Update our ref to track that we've processed this message index
            lastRenderedMessageIndexRef.current = currentIndex;
            
            // Generate a stable ID for this message
            const messageId = lastMessage.id || `booking-${currentIndex}-${Date.now()}`;
            
            // Only process if we haven't seen this message before
            if (!processedBookingMessagesRef.current.has(messageId)) {
                // Mark as processed
                processedBookingMessagesRef.current.add(messageId);
                
                // Use setTimeout instead of requestAnimationFrame for more reliable breaking of the render cycle
                setTimeout(() => {
                    if (lastMessage.events?.[0]) {
                        // We need to ensure this doesn't trigger additional renders that cause loops
                        handleEventSelect(lastMessage.events[0]);
                    }
                }, 0);
            }
        } else {
            // For non-booking messages, still update the ref
            lastRenderedMessageIndexRef.current = currentIndex;
        }
    }, [messages]); // Keep messages as the only dependency
    
    // After everything is loaded, set isInitialLoad to false
    useEffect(() => {
        // Set a short delay to ensure everything is rendered first
        const timer = setTimeout(() => {
            setIsInitialLoad(false);
        }, 500);
        
        return () => clearTimeout(timer);
    }, []);

    // Effect to restore feedback state from localStorage
    useEffect(() => {
        const feedbackKey = 'chat_feedback_state';
        const savedFeedback = localStorage.getItem(feedbackKey);
        
        if (savedFeedback && messages.length > 0) {
            try {
                const feedbackState = JSON.parse(savedFeedback);
                
                // Update messages with their saved feedback state
                setMessages(prev => prev.map(msg => {
                    const msgFeedback = feedbackState[msg.id];
                    if (msgFeedback && msgFeedback.type) {
                        return {
                            ...msg,
                            feedback: msgFeedback.type,
                            feedbackReason: msgFeedback.reason
                        };
                    }
                    return msg;
                }));
            } catch (error) {
                console.error('Error restoring feedback state:', error);
            }
        }
    }, [messages.length]); // Only run when messages length changes

    const addFormattedBotMessage = (content: any) => {
        try {
            let formattedContent;
            let isHtml = false;
            
            console.log("Processing formatted content type:", typeof content);
            console.log("Content sample:", typeof content === 'string' ? content.substring(0, 100) : 'Not a string');
            
            if (!content) {
                // Handle null or undefined content
                formattedContent = "No content available";
            } else if (typeof content === 'string') {
                // String content - could be HTML or plain text
                console.log("Detected string content, checking for HTML");
                
                // Check if it contains HTML tags
                const containsHtml = /<\/?[a-z][\s\S]*>/i.test(content);
                console.log("Contains HTML tags:", containsHtml);
                
                formattedContent = content;
                isHtml = containsHtml;
            } else if (typeof content === 'object') {
                // Convert object to string
                console.log("Detected object content, converting to string");
                formattedContent = JSON.stringify(content);
            } else {
                // Convert anything else to string to be safe
                console.log("Unknown content type, converting to string");
                formattedContent = String(content);
            }
            
            console.log("Is HTML content:", isHtml);
            
            // Add the bot message with the HTML content
            setMessages(prev => [
                ...prev, 
                { 
                    content: formattedContent, 
                    isBot: true, 
                    role: 'assistant',
                    isHtml: isHtml
                }
            ]);
        } catch (error) {
            console.error("Error in addFormattedBotMessage:", error);
            // Add a fallback message if something goes wrong
            setMessages(prev => [
                ...prev, 
                { 
                    content: "I processed your request but had trouble formatting the response.", 
                    isBot: true,
                    role: 'assistant'
                }
            ]);
        }
    };

    // Handle initial message from widget
    useEffect(() => {
        if (initialMessage && clientId) {
            // Add the initial message to the chat
            setMessages(prev => [...prev, initialMessage]);
            
            // Add a small delay to ensure chat interface is fully initialized
            const timer = setTimeout(() => {
                handleSubmit(null, initialMessage.content);
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [initialMessage, clientId]);

    // Add effect to handle page reload during streaming
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Get all streaming messages
            const streamingMessages = messages.filter(msg => msg.isBot && (msg.isStreaming || msg.isLoading));
            
            streamingMessages.forEach(msg => {
                if (clientId) {
                    // Find the corresponding user query
                    const userQueryMessage = messages.find(m => m.id === `user-${msg.id}`);
                    
                    // Save the current state of the streaming message
                    chatHistoryService.trackStreamingMessage(
                        clientId,
                        msg.id,
                        msg.content,
                        userQueryMessage?.content || '',
                        { 
                            ...msg.metadata,
                            isStreaming: true,
                            lastSavedAt: Date.now(),
                            wasInterrupted: true // Mark that this stream was interrupted by reload
                        }
                    );
                }
            });
        };

        // Also handle visibility change for tab switching
        const handleVisibilityChange = () => {
            if (document.hidden) {
                handleBeforeUnload();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [messages, clientId]);

    // Modify restoration effect to handle streaming state better
    useEffect(() => {
        if (clientId) {
            const streams = chatHistoryService.getActiveStreams(clientId);
            if (Object.keys(streams).length > 0) {
                console.log('[ChatInterface] Found active streams:', streams);
                
                // First restore messages to their last known state
                setMessages(prev => {
                    const newMessages = [...prev];
                    Object.entries(streams).forEach(([messageId, stream]) => {
                        // First add the user message if it exists
                        if (stream.userQuery) {
                            const userMessageId = `user-${messageId}`;
                            if (!newMessages.some(m => m.id === userMessageId)) {
                                newMessages.push({
                                    id: userMessageId,
                                    content: stream.userQuery,
                                    isBot: false,
                                    role: 'user',
                                    type: 'text'
                                });
                            }
                        }

                        // Then add or update bot message
                        const botMessageIndex = newMessages.findIndex(m => m.id === messageId);
                        const botMessage = {
                            id: messageId,
                            content: stream.content || '',
                            isBot: true,
                            role: 'assistant',
                            type: 'text',
                            isStreaming: true,
                            isLoading: false,
                            metadata: {
                                ...stream.metadata,
                                originalUserQuery: stream.userQuery // Store original query in metadata
                            }
                        };
                        
                        if (botMessageIndex >= 0) {
                            newMessages[botMessageIndex] = botMessage;
                        } else {
                            newMessages.push(botMessage);
                        }
                    });
                    return newMessages;
                });

                // Then attempt to resume each stream with a small delay between each
                Object.entries(streams).forEach(([messageId, stream], index) => {
                    // Check if we have a user query either in the stream object or metadata
                    const userQuery = stream.userQuery || stream.metadata?.originalUserQuery;
                    
                    if ((stream.isStreaming || stream.metadata?.wasInterrupted) && userQuery) {
                        // Add small delay between stream resumptions to prevent race conditions
                        setTimeout(() => {
                            console.log(`[ChatInterface] Attempting to resume stream for message ${messageId} with query: ${userQuery}`);
                            resumeStream(messageId, userQuery);
                        }, index * 100); // 100ms delay between each stream
                    } else {
                        console.warn(`[ChatInterface] Cannot resume stream for message ${messageId} - missing user query. Stream:`, stream);
                        // Mark the message as failed if we can't resume it
                        setMessages(prev => prev.map(m => 
                            m.id === messageId ? {
                                ...m,
                                isStreaming: false,
                                isLoading: false,
                                isError: true,
                                content: 'Could not resume message - missing original query.'
                            } : m
                        ));
                        // Clean up the stream state
                        chatHistoryService.completeStreamingMessage(clientId, messageId);
                    }
                });
            }
        }
    }, [clientId, isVisible]);

    // Add function to resume stream
    const resumeStream = useCallback((messageId: string, userQuery: string) => {
        if (!clientId || !userQuery.trim()) {
            console.warn(`[ResumeStream] Cannot resume stream - missing ${!clientId ? 'clientId' : 'userQuery'}`);
            return;
        }

        // Abort any existing stream first
        if (streamAbortRef.current) {
            streamAbortRef.current();
            streamAbortRef.current = null;
        }

        console.log(`[ChatInterface] Resuming stream for message ${messageId} with query: ${userQuery}`);

        // Set loading state immediately
        setIsLoading(true);

        // Set loading state in message
        setMessages(prev => prev.map(m => 
            m.id === messageId ? { 
                ...m, 
                isLoading: true,
                isStreaming: true,
                isError: false 
            } : m
        ));

        const abort = createChatStream(
            clientId,
            userQuery,
            (chunk) => {
                // Handle usage limit errors (Free plan blocking)
                if (chunk.type === 'error' && chunk.metadata?.error_code === 'USAGE_LIMIT_REACHED') {
                    console.warn('[ChatInterface] Usage limit reached:', chunk.content);

                    // Remove the user's message since it won't be processed
                    setMessages(prev => prev.filter(msg => msg.id !== messageId));

                    // Show error message with upgrade prompt
                    toast.error(chunk.content, {
                        duration: 8000,
                        style: { maxWidth: '500px' }
                    });

                    // Optionally show an upgrade banner
                    setMessages(prev => [...prev, {
                        id: `limit-error-${Date.now()}`,
                        content: `⚠️ **Usage Limit Reached**\n\n${chunk.content}\n\n[View Plans](/client/billing)`,
                        isBot: true,
                        role: 'assistant',
                        type: 'error',
                        metadata: {
                            error_code: 'USAGE_LIMIT_REACHED',
                            usage_info: chunk.metadata?.usage_info
                        }
                    }]);

                    setIsLoading(false);
                    streamAbortRef.current = null;
                    focusTextarea();
                    return;
                }

                if (chunk.type === 'text_chunk') {
                    // Use flushSync to ensure immediate update
                    flushSync(() => {
                        setIsLoading(true);
                        setMessages(prev => {
                            const messageIndex = prev.findIndex(msg => msg.id === messageId);
                            if (messageIndex === -1) return prev;
                            
                            const newMessages = [...prev];
                            const currentMessage = newMessages[messageIndex];
                            const updatedContent = currentMessage.content + chunk.content;
                            
                            // Create updated message with streaming flag
                            const updatedMessage = {
                                ...currentMessage,
                                content: updatedContent,
                                isStreaming: true,
                                isLoading: false,
                                metadata: { 
                                    ...currentMessage.metadata,
                                    ...chunk.metadata,
                                    lastChunkTime: Date.now(),
                                    isActiveStream: true // Add flag for active streaming
                                }
                            };
                            
                            // Handle clear local storage if needed. Replace the whole
                            // message list with just this bubble and DO NOT fall through
                            // to updateStreamingMessage below — otherwise we'd immediately
                            // re-persist the very history we just cleared (that was the bug
                            // where a cleared chat reappeared after reload). Applies to ANY
                            // clear response, not only the welcome one.
                            if (chunk.metadata?.clear_local_storage) {
                                console.log('[ChatInterface] Clearing local storage based on server response');
                                chatHistoryService.clearHistory(clientId);
                                return [updatedMessage];
                            }

                            // Update local storage
                            chatHistoryService.updateStreamingMessage(
                                clientId,
                                messageId,
                                updatedContent,
                                updatedMessage.metadata
                            );
                            
                            // Update the message in the array
                            newMessages[messageIndex] = updatedMessage;
                            return newMessages;
                        });
                    });
                    
                    // Default to bottom while streaming unless user scrolled up
                    requestAnimationFrame(() => {
                        scrollToBottom('auto');
                    });
                } else if (chunk.metadata?.intent === 'support' && (chunk.type === 'support_step' || chunk.type === 'support_summary' || chunk.type === 'complete' || chunk.type === 'text_chunk')) {
                    processSupportChunks([chunk]);
                    return;
                } else if (chunk.type === 'booking') {
                    // Use flushSync for booking chunks too
                    flushSync(() => {
                        setIsLoading(true);
                        setMessages(prev => {
                            const messageIndex = prev.findIndex(msg => msg.id === messageId);
                            if (messageIndex === -1) return prev;
                            
                            const newMessages = [...prev];
                            const bookingContent = typeof chunk.content === 'object' ? chunk.content : chunk;
                            
                            newMessages[messageIndex] = {
                                ...newMessages[messageIndex],
                                content: bookingContent.text || '',
                                type: 'booking',
                                events: bookingContent.events || [],
                                timezone: bookingContent.timezone || 'UTC',
                                user_timezone: bookingContent.user_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                isStreaming: true,
                                isLoading: false,
                                showCalendar: true,
                                metadata: {
                                    ...chunk.metadata,
                                    isActiveStream: true
                                }
                            };
                            
                            return newMessages;
                        });
                    });
                }
            },
            (complete) => {
                if (complete.type === 'booking') {
                    // Handle booking type messages in completion
                    setMessages(prev => prev.map(msg => {
                        if (msg.id === messageId) {
                            // The booking content might be in complete.content directly
                            const bookingContent = typeof complete.content === 'object' ? complete.content : complete;
                            console.log('[ChatInterface] Processing booking completion:', bookingContent);
                            
                            return {
                                ...msg,
                                content: bookingContent.text || '',
                                type: 'booking',
                                events: bookingContent.events || [],
                                timezone: bookingContent.timezone || 'UTC',
                                user_timezone: bookingContent.user_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                isStreaming: false,
                                isLoading: false,
                                showCalendar: true,
                                metadata: complete.metadata
                            };
                        }
                        return msg;
                    }));
                    setIsLoading(false);
                    streamAbortRef.current = null;
                    focusTextarea();
                    return;
                }

                // Check if we need to clear local storage based on metadata
                if (complete.metadata?.clear_local_storage) {
                    console.log('[ChatInterface] Clearing local storage based on server response (completion)');
                    chatHistoryService.clearHistory(clientId);
                    
                    // If this is a welcome message, keep only this message
                    setMessages([{
                        id: messageId,
                        content: complete.content,
                        isBot: true,
                        role: 'assistant',
                        type: 'text',
                        isStreaming: false,
                        isLoading: false,
                        metadata: complete.metadata
                    }]);
                    setIsLoading(false);
                    streamAbortRef.current = null;
                    focusTextarea();
                    return;
                }

                // Handle regular message completion
                setMessages(prev => prev.map(msg => {
                    if (msg.id === messageId) {
                        chatHistoryService.completeStreamingMessage(clientId, messageId);
                        return {
                            ...msg,
                            content: complete.content,
                            citation_sources: complete.metadata?.citation_sources || complete.citation_sources,
                            isStreaming: false,
                            isLoading: false,
                            metadata: {
                                ...complete.metadata,
                                conversation_id: complete.metadata?.conversation_id || conversationId
                            }
                        };
                    }
                    return msg;
                }));
                setIsLoading(false);
                streamAbortRef.current = null;
                focusTextarea();
            },
            (error) => {
                console.error('[ResumeStream] error:', error);
                setMessages(prev => prev.map(m => 
                    m.id === messageId ? { 
                        ...m, 
                        isStreaming: false, 
                        isLoading: false,
                        content: m.content || 'Stream could not be resumed. Please try sending your message again.',
                        metadata: {
                            ...m.metadata,
                            wasInterrupted: false,
                            originalUserQuery: userQuery
                        }
                    } : m
                ));
                setIsLoading(false);  // Set loading to false on error
                streamAbortRef.current = null;
                focusTextarea();
            },
            { conversation_id: conversationId }
        );

        streamAbortRef.current = abort;
    }, [clientId]);

    const handleClearConversation = async () => {
        try {
            // Send "clear" as a message to trigger soft delete
            await handleSubmit(undefined, 'clear');
            // Generate a new conversation ID for the next conversation
            setConversationId(generateUUID());
            setActiveSupportSession(null);
            setSupportSubmitting(false);
            // The backend will handle the soft delete and the UI will update via the storage event listener
        } catch (error) {
            console.error('Error clearing conversation:', error);
            toast.error('Failed to clear conversation');
        }
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        console.log('[IMAGE-UPLOAD] Files selected:', files.length);

        const maxSize = (currentImageStep?.max_size_mb || 10) * 1024 * 1024;
        const newFiles: File[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Check file size
            if (file.size > maxSize) {
                toast.error(`File "${file.name}" exceeds maximum size of ${currentImageStep?.max_size_mb || 10}MB`);
                continue;
            }

            newFiles.push(file);
        }

        if (newFiles.length > 0) {
            if (currentImageStep?.multiple) {
                setUploadedImages(prev => [...prev, ...newFiles]);
            } else {
                setUploadedImages(newFiles.slice(0, 1));
            }
            console.log('[IMAGE-UPLOAD] Added to uploadedImages, new count:', newFiles.length);
            toast.success(`${newFiles.length} image(s) selected`);
        }
    };

    const handleRemoveImage = (index: number) => {
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleSendImages = async () => {
        console.log('[IMAGE-SEND] handleSendImages called, uploadedImages:', uploadedImages.length, 'currentImageStep:', currentImageStep);
        if (!uploadedImages.length || !currentImageStep) return;

        setIsLoading(true);

        // Convert files to base64
        const fileData = await Promise.all(
            uploadedImages.map(async (file) => {
                const base64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
                return {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    data: base64
                };
            })
        );

        // Use user's typed message, or default message if empty
        const userMessage = inputMessage.trim() || `[Uploaded ${uploadedImages.length} image(s)]`;

        // Add user message to UI
        setMessages(prev => [...prev, {
            id: `user-${Date.now()}`,
            content: userMessage,
            isBot: false,
            role: 'user',
            metadata: {
                conversation_id: conversationId,
                image_upload: {
                    step_id: currentImageStep.step_id,
                    files: fileData
                }
            }
        }]);

        // Prepare metadata for backend
        const imageMetadata = {
            image_upload: {
                step_id: currentImageStep.step_id,
                files: fileData
            }
        };

        // DON'T clear uploads yet - keep preview visible during upload
        // They'll be cleared when response completes

        // Send to backend with image metadata
        handleSubmit(undefined, userMessage, imageMetadata);
    };

    const handleSubmit = async (e?: React.FormEvent, messageContent?: string, extraMetadata?: Record<string, any>) => {
        if (e) e.preventDefault();

        console.log('[HANDLE-SUBMIT] Called with:', {
            hasEvent: !!e,
            messageContent,
            extraMetadata,
            uploadedImagesLength: uploadedImages.length
        });

        const userMessageContent = messageContent || inputMessage;
        if (!userMessageContent.trim() || isLoading || !clientId) return;

        // Cleanup previous stream if exists
        if (streamAbortRef.current) {
            streamAbortRef.current();
            streamAbortRef.current = null;
        }

        setInputMessage('');
        setIsLoading(true);

        // Generate message IDs
        const userMessageId = `user-${Date.now()}`;
        const botMessageId = `bot-${Date.now()}`;

        // Add user message if not provided (i.e., not from widget)
        if (!messageContent) {
            setMessages(prev => [...prev, {
                id: userMessageId,
                content: userMessageContent,
                isBot: false,
                role: 'user',
                type: 'text'
            }]);
        }

        // Add initial bot message
        setMessages(prev => [
            ...prev,
            {
                id: botMessageId,
                content: "",
                isBot: true, 
                role: 'assistant',
                type: 'text',
                isLoading: true,
                isStreaming: false,
                metadata: {
                    conversation_id: conversationId
                }
            }
        ]);

        try {
            // Track the streaming message in local storage with initial empty content
            chatHistoryService.trackStreamingMessage(
                clientId,
                botMessageId,
                "",
                userMessageContent,
                { isStreaming: true }
            );

            console.log('[CHAT-STREAM] Setting up createChatStream with onComplete callback');
            console.log('[CHAT-STREAM] extraMetadata being passed:', extraMetadata);
            const abortStream = createChatStream(
                clientId,
                userMessageContent,
                (chunk) => {
                    if (chunk.type === 'text_chunk') {
                        // Use flushSync to ensure immediate update
                        flushSync(() => {
                            setIsLoading(true);
                            setMessages(prev => {
                                const messageIndex = prev.findIndex(msg => msg.id === botMessageId);
                                if (messageIndex === -1) {
                                    // The bot bubble was removed (e.g. a history-reload
                                    // re-run clobbered it). Re-add it so the streaming
                                    // response is never silently dropped — this is the
                                    // first-message-after-reload "no answer" bug.
                                    return [...prev, {
                                        id: botMessageId,
                                        content: chunk.content || '',
                                        isBot: true,
                                        role: 'assistant',
                                        type: 'text',
                                        isStreaming: true,
                                        isLoading: false,
                                        metadata: {
                                            ...chunk.metadata,
                                            conversation_id: chunk.metadata?.conversation_id || conversationId,
                                            lastChunkTime: Date.now(),
                                            isActiveStream: true
                                        }
                                    }];
                                }

                                const newMessages = [...prev];
                                const currentMessage = newMessages[messageIndex];
                                const updatedContent = currentMessage.content + chunk.content;

                                // Create updated message with streaming flag
                                const updatedMessage = {
                                    ...currentMessage,
                                    content: updatedContent,
                                    isStreaming: true,
                                    isLoading: false,
                                    metadata: {
                                        ...currentMessage.metadata,
                                        ...chunk.metadata,
                                        conversation_id: chunk.metadata?.conversation_id || conversationId,
                                        lastChunkTime: Date.now(),
                                        isActiveStream: true // Add flag for active streaming
                                    }
                                };
                                
                                // Handle clear local storage if needed. Replace with just
                                // this bubble and DON'T fall through to re-persist (same
                                // cleared-chat-reappears-after-reload fix as the other path).
                                if (chunk.metadata?.clear_local_storage) {
                                    console.log('[ChatInterface] Clearing local storage based on server response');
                                    chatHistoryService.clearHistory(clientId);
                                    return [updatedMessage];
                                }

                                // Update local storage
                                chatHistoryService.updateStreamingMessage(
                                    clientId,
                                    botMessageId,
                                    updatedContent,
                                    updatedMessage.metadata
                                );
                                
                                // Update the message in the array
                                newMessages[messageIndex] = updatedMessage;
                                return newMessages;
                            });
                        });
                        
                        // Default to bottom while streaming unless user scrolled up
                        requestAnimationFrame(() => {
                            scrollToBottom('auto');
                        });
                    } else if (chunk.metadata?.intent === 'support' && (chunk.type === 'support_step' || chunk.type === 'support_summary' || chunk.type === 'text_chunk')) {
                        // Process support chunks EXCEPT 'complete' type (handled by onComplete callback)
                        processSupportChunks([chunk]);
                        return;
                    } else if (chunk.type === 'booking') {
                        // Use flushSync for booking chunks too
                        flushSync(() => {
                            setIsLoading(true);
                            setMessages(prev => {
                                const messageIndex = prev.findIndex(msg => msg.id === botMessageId);
                                if (messageIndex === -1) return prev;
                                
                                const newMessages = [...prev];
                                const bookingContent = typeof chunk.content === 'object' ? chunk.content : chunk;
                                
                                newMessages[messageIndex] = {
                                    ...newMessages[messageIndex],
                                    content: bookingContent.text || '',
                                    type: 'booking',
                                    events: bookingContent.events || [],
                                    timezone: bookingContent.timezone || 'UTC',
                                    user_timezone: bookingContent.user_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                    isStreaming: true,
                                    isLoading: false,
                                    showCalendar: true,
                                    metadata: {
                                        ...chunk.metadata,
                                        isActiveStream: true
                                    }
                                };
                                
                                return newMessages;
                            });
                        });
                    }
                },
                (complete) => {
                    console.log('[SUPPORT-COMPLETE] Complete callback fired:', {
                        type: complete.type,
                        intent: complete.metadata?.intent,
                        imageUploadNeeded: complete.metadata?.image_upload_needed,
                        imageStep: complete.metadata?.image_step,
                        fullMetadata: complete.metadata,
                        fullComplete: complete
                    });

                    // Handle support flow completion
                    if (complete.metadata?.intent === 'support') {
                        console.log('[SUPPORT-COMPLETE] Processing support complete event');
                        console.log('[SUPPORT-COMPLETE] Condition check:', {
                            hasImageUploadNeeded: !!complete.metadata?.image_upload_needed,
                            imageUploadNeededValue: complete.metadata?.image_upload_needed,
                            hasImageStep: !!complete.metadata?.image_step,
                            imageStepValue: complete.metadata?.image_step,
                            bothTrue: !!(complete.metadata?.image_upload_needed && complete.metadata?.image_step)
                        });

                        // Support complete is now handled inside processSupportChunks during text_chunk processing
                        // Just set image upload state and cleanup
                        if (complete.metadata?.image_upload_needed && complete.metadata?.image_step) {
                            console.log('[SUPPORT-COMPLETE] ✅ Setting image upload state to TRUE', complete.metadata.image_step);
                            setImageUploadEnabled(true);
                            setCurrentImageStep(complete.metadata.image_step);
                        } else {
                            console.log('[SUPPORT-COMPLETE] ❌ Setting image upload state to FALSE - condition not met');
                            setImageUploadEnabled(false);
                            setCurrentImageStep(null);
                        }
                        setIsLoading(false);
                        streamAbortRef.current = null;
                        focusTextarea();
                        console.log('[SUPPORT-COMPLETE] Returning early - should NOT execute general completion code');
                        return;
                    }

                    if (complete.type === 'booking') {
                        // Handle booking type messages in completion
                        setMessages(prev => prev.map(msg => {
                            if (msg.id === botMessageId) {
                                // The booking content might be in complete.content directly
                                const bookingContent = typeof complete.content === 'object' ? complete.content : complete;
                                console.log('[ChatInterface] Processing booking completion:', bookingContent);
                                
                                return {
                                    ...msg,
                                    content: bookingContent.text || '',
                                    type: 'booking',
                                    events: bookingContent.events || [],
                                    timezone: bookingContent.timezone || 'UTC',
                                    user_timezone: bookingContent.user_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                    isStreaming: false,
                                    isLoading: false,
                                    showCalendar: true,
                                    metadata: complete.metadata
                                };
                            }
                            return msg;
                        }));
                        setIsLoading(false);
                        streamAbortRef.current = null;
                        focusTextarea();
                        return;
                    }

                    // Check if we need to clear local storage based on metadata
                    if (complete.metadata?.clear_local_storage) {
                        console.log('[ChatInterface] Clearing local storage based on server response (completion)');
                        chatHistoryService.clearHistory(clientId);
                        
                        // If this is a welcome message, keep only this message
                        setMessages([{
                            id: botMessageId,
                            content: complete.content,
                            isBot: true,
                            role: 'assistant',
                            type: 'text',
                            isStreaming: false,
                            isLoading: false,
                            metadata: complete.metadata
                        }]);
                        setIsLoading(false);
                        streamAbortRef.current = null;
                        focusTextarea();
                        return;
                    }

                    // Handle regular message completion
                    console.log('[GENERAL-COMPLETE] Handling regular (non-support) completion');
                    setMessages(prev => prev.map(msg => {
                        if (msg.id === botMessageId) {
                            chatHistoryService.completeStreamingMessage(clientId, botMessageId);
                            return {
                                ...msg,
                                content: complete.content,
                                citation_sources: complete.metadata?.citation_sources || complete.citation_sources,
                                isStreaming: false,
                                isLoading: false,
                                metadata: complete.metadata
                            };
                        }
                        return msg;
                    }));
                    setIsLoading(false);
                    streamAbortRef.current = null;

                    // Clear uploaded images after successful send
                    console.log('[GENERAL-COMPLETE] Clearing image upload state (general chat completion)');
                    setUploadedImages([]);
                    setImageUploadEnabled(false);
                    setCurrentImageStep(null);

                    focusTextarea();
                },
                (error) => {
                    console.error('Error in chat stream:', error);
                    setMessages(prev => prev.map(msg => 
                        msg.id === botMessageId ? {
                            ...msg,
                            content: "Sorry, an error occurred during streaming.",
                            isError: true,
                            isStreaming: false,
                            isLoading: false
                        } : msg
                    ));
                    chatHistoryService.completeStreamingMessage(clientId, botMessageId);
                    setIsLoading(false);
                    streamAbortRef.current = null;
                },
                {
                    conversation_id: conversationId,
                    ...extraMetadata
                }
            );

            streamAbortRef.current = abortStream;
            setStreamAbortController(() => abortStream);
        } catch (error) {
            console.error('Error in chat stream:', error);
            setIsLoading(false);
            chatHistoryService.completeStreamingMessage(clientId, botMessageId);
            streamAbortRef.current = null;
        }
    };

    const handleDateSelect = (date: Date) => {
        setSelectedDate(date);
    };

    const handleSlotSelect = async (slot: Slot) => {
        const formattedDate = new Date(slot.start).toLocaleString();
        const message = `I would like to book this time slot: ${formattedDate}`;
        setInputMessage(message);
    };

    const handleEventTypeSelect = async (eventType: EventType) => {
        // Format message for selecting event type
        const message = `I would like to book a ${eventType.title} (${eventType.length} minutes)`;
        setInputMessage(message);
    };

    // Helper function to check if a message has citations
    const hasCitations = (message) => {
        return !!(message.citation_sources || 
                 (message.message?.citation_sources) || 
                 (typeof message.content === 'string' && 
                  message.content.includes('<sup') && 
                  message.content.includes('</sup>')));
    };

    // Helper function to sanitize HTML content
    const sanitizeHtml = (html) => {
        if (!html || typeof html !== 'string') return 'No content to display';
        
        // Check if the content appears to be raw HTML message bubble content
        if (html.includes('<div class="flex max-w-[80%]"') || 
            html.includes('<div class="flex-shrink-0 h-8 w-8 rounded-full"')) {
            // This is likely a raw HTML representation of a message bubble
            // Extract just the text content
            const textMatch = html.match(/<p class="whitespace-pre-wrap break-words">(.*?)<\/p>/);
            if (textMatch && textMatch[1]) {
                return textMatch[1];
            }
        }
        
        return html
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/on\w+="[^"]*"/g, '') // Remove inline event handlers
            .replace(/javascript:/gi, ''); // Remove javascript: URLs
    };

    const handleSelectSlot = (slot: Slot) => {
        setSelectedSlot(slot);
    };

    const handleInitiateBooking = () => {
        if (!selectedSlot) {
            toast.error("Please select a time slot first");
            return;
        }
        
        // Show the booking form to collect user details
        setShowBookingForm(true);
    };

    const handleSendMessage = async (message: string) => {
        if (!message.trim()) return;
        
        setIsLoading(true);
        
        // Add user message to chat
        const userMessage = {
            content: message,
            isBot: false,
            role: 'user'
        };
        
        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        
        try {
            const apiResponse = await sendChatMessage(clientId!, message);
            console.log("Full API Response:", apiResponse);
            
            // Extract the response from the nested structure
            const responseData = apiResponse.response || apiResponse;
            console.log("Response Data Structure:", JSON.stringify(responseData, null, 2));
            
            if (responseData && responseData.type === 'booking') {
                console.log("Found booking data:");
                console.log("- Text:", responseData.text);
                console.log("- Events:", responseData.events?.length || 0);
                console.log("- Timezone:", responseData.timezone);
                
                // Create a complete message object with the proper booking data
                const bookingMessage = {
                    content: responseData.text,
                    isBot: true,
                    role: 'assistant',
                    type: 'booking',
                    // Preserve the complete events array exactly as received
                    CalendarEvent: responseData.events,
                    timezone: responseData.timezone
                };
                
                console.log("Created booking message:", bookingMessage);
                setMessages(prev => [...prev, bookingMessage]);
            } else {
                // Add a formatted message if the response is complex
                if (responseData.formattedResponse) {
                    try {
                        console.log("Received formatted response:", responseData.formattedResponse);
                        addFormattedBotMessage(responseData.formattedResponse);
                    } catch (formatError) {
                        console.error('Error processing formatted response:', formatError);
                        // Fallback to plain text if there's an error processing the formatted response
                        setMessages(prev => [...prev, { 
                            content: responseData?.text || "I have some information to share, but couldn't format it properly.", 
                            isBot: true 
                        }]);
                    }
                } else {
                    // Or use the regular text response
                    setMessages(prev => [...prev, { content: responseData?.text || "No response", isBot: true }]);
                }
            }
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message. Please try again.');
            
            setMessages(prev => [...prev, {
                content: "I'm sorry, I couldn't process your request. Please try again.",
                isBot: true,
                role: 'assistant'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEventSelect = (event: any) => {
        if (!event) return;
        
        // Use functional updates to avoid stale closures
        setSelectedEvent(event);
        // Reset related state
        setSelectedDate(null);
        setSelectedSlot(null);
    };

    const handleFeedback = async (messageId: string, type: 'thumbs_up' | 'thumbs_down' | 'need_review', reason?: string) => {
        try {
            // Find the conversation_id for this message
            let conversation_id = null;
            
            // Look through messages to find the conversation_id
            for (const message of messages) {
                if (message.id === messageId) {
                    conversation_id = message.metadata?.conversation_id || conversationId;
                    console.log(`Found message ${messageId} with conversation_id: ${conversation_id}`);
                    break;
                }
            }
            
            // If still no conversation_id, use the component's conversationId
            if (!conversation_id) {
                conversation_id = conversationId;
                console.log(`Using component conversation_id: ${conversation_id} for message ${messageId}`);
            }
            
            await feedbackService.submitFeedback({
                message_id: messageId,
                feedback_type: type,
                reason: reason,
                client_id: clientId,
                conversation_id: conversation_id,
            });
            
            // Update the message's feedback state in the UI
            setMessages(prev => prev.map(msg => 
                msg.id === messageId 
                    ? { ...msg, feedback: type, feedbackReason: reason }
                    : msg
            ));
            
            // Save feedback state to localStorage for persistence
            const feedbackKey = 'chat_feedback_state';
            const existingFeedback = JSON.parse(localStorage.getItem(feedbackKey) || '{}');
            existingFeedback[messageId] = {
                type,
                reason: reason || null,
                timestamp: new Date().toISOString()
            };
            localStorage.setItem(feedbackKey, JSON.stringify(existingFeedback));
            
            console.log(`Feedback submitted: ${type} for message ${messageId}${reason ? ` with reason: ${reason}` : ''}`);
            toast.success('Thank you for your feedback!');
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Failed to submit feedback. Please try again.');
        }
    };

    const handleTimeSelect = (event: any, date: Date, time: string) => {
        const selectedDateTime = `${format(date, 'yyyy-MM-dd')}T${time}`;
        setSelectedSlot({
            start: selectedDateTime,
            end: selectedDateTime,
            event: event
        });
    };

    // Optimize fetchBookedSlotsIfNeeded to prevent unnecessary state updates
    const fetchBookedSlotsIfNeeded = useCallback(async (forceRefresh = false) => {
        // Return early if we're already loading or if there's no clientId
        if (isLoadingBookedSlots || !clientId) {
            return;
        }
        
        const shouldRefresh = !bookedSlots || forceRefresh || 
            (bookedSlotsLoadedAt && Date.now() - bookedSlotsLoadedAt > 120000);
            
        if (!shouldRefresh) {
            return;
        }
        
        try {
            setIsLoadingBookedSlots(true);
            const data = await getCalendarBookings(clientId);
            const timezone = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            const formattedData: BookedSlotsData = {
                status: 'success',
                data: {
                    booked_times_by_day: data.booked_times_by_day || {},
                    timezone: timezone
                }
            };
            setBookedSlots(formattedData);
            setBookedSlotsLoadedAt(Date.now());
        } catch (error) {
            console.error('[ChatInterface] Error fetching booked slots:', error);
        } finally {
            setIsLoadingBookedSlots(false);
        }
    }, [clientId, bookedSlots, bookedSlotsLoadedAt, isLoadingBookedSlots, userTimezone]);

    // Update the forceRefreshCalendar function to use our centralized fetch function
    const forceRefreshCalendar = useCallback((messageIndex: number) => {
        // Prevent multiple simultaneous refreshes
        if (calendarRefreshingRef.current) return;
        calendarRefreshingRef.current = true;
        
        // Make just this specific calendar visible
        setMessages(prevMessages => 
            prevMessages.map((msg, idx) => {
                if (idx === messageIndex && msg.type === 'booking') {
                    return {
                        ...msg,
                        showCalendar: true,
                        refreshTimestamp: Date.now() // Add a timestamp to force refresh
                    };
                } else if (msg.type === 'booking' && msg.showCalendar) {
                    // Hide other calendars to prevent multiple visible calendars
                    return {
                        ...msg,
                        showCalendar: false
                    };
                }
                return msg;
            })
        );
        
        // Fetch booked slots if needed, doesn't force refresh
        fetchBookedSlotsIfNeeded().then(() => {
            // Reset flag when complete
            setTimeout(() => {
                calendarRefreshingRef.current = false;
            }, 300);
        });
    }, [fetchBookedSlotsIfNeeded]);

    // Update the forceRefreshCalendars function
    const forceRefreshCalendars = useCallback(() => {
        // Prevent multiple simultaneous refreshes
        if (calendarRefreshingRef.current) return;
        calendarRefreshingRef.current = true;
        
        // Find the most recent calendar message
        const calendarMessages = messages.map((msg, idx) => ({ msg, idx }))
            .filter(item => item.msg.type === 'booking');
        
        if (calendarMessages.length > 0) {
            // Get the most recent calendar message
            const mostRecentCalendar = calendarMessages[calendarMessages.length - 1];
            
            // Update just the most recent message to be visible
            setMessages(prevMessages => 
                prevMessages.map((msg, idx) => {
                    if (idx === mostRecentCalendar.idx) {
                        return {
                            ...msg,
                            showCalendar: true,
                            refreshTimestamp: Date.now() // Add a timestamp to force refresh
                        };
                    } else if (msg.type === 'booking' && msg.showCalendar) {
                        // Hide other calendars
                        return {
                            ...msg,
                            showCalendar: false
                        };
                    }
                    return msg;
                })
            );
            
            // Update the counter to force re-render
            setCalendarRefreshCounter(prev => prev + 1);
        }
        
        // Fetch booked slots if needed, doesn't force refresh
        fetchBookedSlotsIfNeeded().then(() => {
            // Reset flag when complete
            setTimeout(() => {
                calendarRefreshingRef.current = false;
            }, 300);
        });
    }, [messages, fetchBookedSlotsIfNeeded]);

    // Update handleBookingSuccess to force refresh booked slots
    const handleBookingSuccess = useCallback(async (response: any) => {
        if (clientId) {
            try {
                // Always force refresh booked slots after a successful booking
                await fetchBookedSlotsIfNeeded(true); // Force refresh
                
                // Trigger a refresh of time slots in any active calendar views
                if (lastActiveDate && lastActiveEvent) {
                    setSlotsNeedRefresh(true);
                }

                // Show success message
                setMessages(prev => [...prev, {
                    id: `booking-success-${Date.now()}`,
                    content: `Great! Your booking has been confirmed. You'll receive a confirmation email with all the details.`,
                    isBot: true,
                    role: 'assistant',
                    isConfirmation: true
                }]);

                // Reset form state
                setShowBookingForm(false);
                setSelectedEvent(null);
                setSelectedTime(null);
                setSelectedDate(null);
            } catch (error) {
                console.error('Error refreshing booked slots:', error);
            }
        }
    }, [clientId, fetchBookedSlotsIfNeeded, lastActiveDate, lastActiveEvent]);

    // Update the initial effect for loading calendars
    useEffect(() => {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage?.type === 'booking' && clientId && !bookedSlots && !isLoadingBookedSlots) {
            // Fetch booked slots only once when calendar message is received
            fetchBookedSlotsIfNeeded();
        }
    }, [messages, clientId, bookedSlots, isLoadingBookedSlots, fetchBookedSlotsIfNeeded]);

    // Only update UI when booked slots data is available
    useEffect(() => {
        if (bookedSlots) {
            // Only update UI when booked slots data is available
            setCalendarRefreshCounter(prev => prev + 1);
        }
    }, [bookedSlots]);

    // Optimize ensureLatestCalendarVisibility to prevent unnecessary state updates
    const ensureLatestCalendarVisibility = useCallback(() => {
        // Prevent operation if already refreshing
        if (calendarRefreshingRef.current) return;
        
        const calendarMessages = messages.map((msg, idx) => ({ msg, idx }))
            .filter(item => item.msg.type === 'booking');
        
        if (calendarMessages.length > 0) {
            const mostRecentIndex = calendarMessages[calendarMessages.length - 1].idx;
            
            // Check if we actually need to make changes
            const needsUpdates = messages.some((msg, idx) => 
                msg.type === 'booking' && 
                ((idx === mostRecentIndex && !msg.showCalendar) || 
                 (idx !== mostRecentIndex && msg.showCalendar))
            );
            
            if (needsUpdates) {
                calendarRefreshingRef.current = true;
                
                setMessages(prevMessages => 
                    prevMessages.map((msg, idx) => {
                        if (msg.type === 'booking') {
                            const shouldBeVisible = idx === mostRecentIndex;
                            if (shouldBeVisible !== msg.showCalendar) {
                                return {
                                    ...msg,
                                    showCalendar: shouldBeVisible,
                                    ...(shouldBeVisible ? { refreshTimestamp: Date.now() } : {})
                                };
                            }
                        }
                        return msg;
                    })
                );
                
                // Reset the flag after a delay
                setTimeout(() => {
                    calendarRefreshingRef.current = false;
                }, 300);
            }
            
            // Only fetch booked slots if we haven't already loaded them
            if (!bookedSlots && !isLoadingBookedSlots) {
                fetchBookedSlotsIfNeeded();
            }
        }
    }, [messages, fetchBookedSlotsIfNeeded, bookedSlots, isLoadingBookedSlots]);

    // Call the cleanup function only when isInitialLoad changes from true to false
    useEffect(() => {
        if (!isInitialLoad) {
            ensureLatestCalendarVisibility();
        }
    }, [isInitialLoad, ensureLatestCalendarVisibility]);

    // Fix the useEffect for refreshing time slots with stable deps
    useEffect(() => {
        if (!slotsNeedRefresh || !bookedSlots || !lastActiveDate || !lastActiveEvent) {
            return;
        }
        
        setSlotsNeedRefresh(false);
        
        const shouldUpdateDate = lastActiveDate !== selectedDate;
        const shouldUpdateEvent = lastActiveEvent !== selectedEvent;
        
        if (shouldUpdateDate) {
            setSelectedDate(lastActiveDate);
        }
        
        if (shouldUpdateEvent) {
            setSelectedEvent(lastActiveEvent);
        }
        
        if (shouldUpdateDate || shouldUpdateEvent) {
            setCalendarRefreshCounter(prev => prev + 1);
        }
    }, [slotsNeedRefresh, bookedSlots, lastActiveDate, lastActiveEvent, selectedDate, selectedEvent]);

    const renderMessageContent = (message: Message, messageIndex: number) => {
        if (message.type === 'support_session' && message.supportSession) {
            return (
                <SupportSessionMessage
                    session={message.supportSession}
                    submitting={supportSubmitting && activeSupportSession === message.supportSession.sessionId}
                    onSubmitStep={handleSupportStepSubmit}
                    variant="default"
                />
            );
        }

        if (!message.isUser && (
            (message.metadata && message.metadata.use_formatted_response) ||
            (isWidgetMode && typeof message.content === 'string' && message.content.includes('<'))
        )) {
            const contentToSanitize = typeof message.content === 'string' ? message.content : String(message.content);
            const cleanedContent = contentToSanitize.replace(/<([^>]+)<|<[^>]*$/g, '');

            return (
                <div
                    className="message-content prose prose-invert max-w-none prose-p:my-2 prose-headings:my-3"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(cleanedContent) }}
                />
            );
        }

        // Default - simple text content rendering with streaming support
        return (
            <div className="message-content whitespace-pre-wrap">
                {message.content}
                {message.isStreaming && (
                    <div className="typing-indicator">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                    </div>
                )}
            </div>
        );
    };

    const handleEventTypeChange = async (eventType) => {
        // Update selected event type
        setCalendarData(prevData => ({
            ...prevData,
            selectedEventType: eventType,
            availableSlots: [] // Clear previous slots when changing event type
        }));
        
        // Fetch available slots for the selected event type and date range
        if (selectedDate) {
            fetchAvailableSlots(eventType.id, selectedDate);
        }
    };

    const fetchAvailableSlots = async (eventTypeId, date) => {
        try {
            // Format date as YYYY-MM-DD
            const formattedDate = date.toISOString().split('T')[0];
            
            // Calculate end date (usually the next day or week)
            const endDate = new Date(date);
            endDate.setDate(date.getDate() + 7); // Get a week of availability
            const formattedEndDate = endDate.toISOString().split('T')[0];
            
            // Make API call to get available slots
            const response = await fetch(
                `/api/event_types/${eventTypeId}/availability?start_date=${formattedDate}&end_date=${formattedEndDate}`
            );
            
            if (!response.ok) {
                throw new Error('Failed to fetch available slots');
            }
            
            const slotsData = await response.json();
            
            // Update calendar data with available slots
            setCalendarData(prevData => ({
                ...prevData,
                availableSlots: slotsData
            }));
        } catch (error) {
            console.error('Error fetching available slots:', error);
            // Handle error state
        }
    };

    const formatSupportAnswer = (step: SupportStepDefinition, answer: any): string => {
        switch (step.type) {
            case 'single_select': {
                const label = step.options?.find(opt => opt.value === answer)?.label || answer;
                return `${step.title}: ${label}`;
            }
            case 'multi_select': {
                if (!Array.isArray(answer)) return `${step.title}: ${answer}`;
                const labels = answer.map((value: string) => {
                    const option = step.options?.find(opt => opt.value === value);
                    return option?.label || value;
                });
                return `${step.title}: ${labels.join(', ')}`;
            }
            case 'long_text':
            case 'text':
                return `${step.title}: ${answer}`;
            case 'contact': {
                const pieces = (step.fields || []).map(field => {
                    const value = (answer && answer[field.id]) || '';
                    if (!value) return null;
                    return `${field.label}: ${value}`;
                }).filter(Boolean);
                return pieces.join(' | ');
            }
            default:
                return `${step.title}: ${answer}`;
        }
    };

    const processSupportChunks = useCallback((chunks: any[] = []) => {
        if (!chunks || chunks.length === 0) {
            setIsLoading(false);
            setSupportSubmitting(false);
            return;
        }

        let streamingMessageId: string | null = null;
        let streamingBuffer = '';
        let pendingSupportIntro: { conversationId?: string; content: string } | null = null;

        chunks.forEach(chunk => {
            if (!chunk) return;

            const isSupportIntent = chunk.metadata?.intent === 'support';
            const conversationId = chunk.metadata?.conversation_id;

            if (chunk.type === 'thinking') {
                return;
            }

            if (chunk.type === 'support_step') {
                const payload = chunk.metadata?.support_step as SupportStepPayload;
                if (!payload) return;

                const introText = pendingSupportIntro && pendingSupportIntro.conversationId === conversationId
                    ? pendingSupportIntro.content
                    : chunk.metadata?.intro_text;

                pendingSupportIntro = null;

                setActiveSupportSession(payload.session_id || null);

                setMessages(prev => {
                    let working = [...prev];
                    let found = false;

                    working = working.map(msg => {
                        if (msg.type === 'support_session' && msg.supportSession?.sessionId === payload.session_id) {
                            found = true;
                            return {
                                ...msg,
                                supportSession: {
                                    ...msg.supportSession,
                                    title: payload.flow_title || msg.supportSession.title,
                                    description: payload.flow_description || msg.supportSession.description,
                                    introText: msg.supportSession.introText || introText,
                                    currentStep: payload,
                                    status: 'in_progress',
                                    awaitingNext: false
                                },
                                metadata: { ...msg.metadata, conversation_id: conversationId, intent: 'support' }
                            };
                        }
                        return msg;
                    });

                    if (!found) {
                        working.push({
                            id: `support-session-${payload.session_id}`,
                            type: 'support_session',
                            content: '',
                            isBot: true,
                            role: 'assistant',
                            metadata: { ...chunk.metadata, conversation_id: conversationId, intent: 'support' },
                            supportSession: {
                                sessionId: payload.session_id,
                                title: payload.flow_title,
                                description: payload.flow_description,
                                introText,
                                responses: [],
                                currentStep: payload,
                                summary: undefined,
                                status: 'in_progress',
                                awaitingNext: false,
                                finalMessage: undefined
                            }
                        });
                    }

                    return working;
                });

                streamingMessageId = null;
                streamingBuffer = '';
                return;
            }

            if (chunk.type === 'support_summary') {
                const summary = (chunk.metadata && chunk.metadata.support_summary) || chunk.support_summary || chunk.content;
                if (summary) {
                    const summaryPayload = summary as SupportSummaryPayload;
                    const sessionId = summaryPayload.session_id || activeSupportSessionRef.current;
                    if (sessionId) {
                        setMessages(prev =>
                            prev.map(msg => {
                                if (msg.type === 'support_session' && msg.supportSession?.sessionId === sessionId) {
                                    return {
                                        ...msg,
                                        supportSession: {
                                            ...msg.supportSession,
                                            summary: summaryPayload,
                                            status: 'completed',
                                            currentStep: undefined,
                                            awaitingNext: false
                                        }
                                    };
                                }
                                return msg;
                            })
                        );
                    }
                    setActiveSupportSession(null);
                }
                pendingSupportIntro = null;
                return;
            }

            if (isSupportIntent && chunk.type === 'text_chunk') {
                // Treat support text_chunks like normal text streaming
                const text = chunk.content || '';
                if (!text) return;

                if (!streamingMessageId) {
                    streamingMessageId = `bot-${Date.now()}`;
                    streamingBuffer = text;
                    setMessages(prev => [
                        ...prev,
                        {
                            id: streamingMessageId,
                            type: 'text',
                            content: text,
                            isBot: true,
                            role: 'assistant',
                            isStreaming: true,
                            metadata: { ...chunk.metadata }
                        }
                    ]);
                } else {
                    streamingBuffer += text;
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === streamingMessageId
                                ? { ...msg, content: streamingBuffer }
                                : msg
                        )
                    );
                }
                return;
            }

            // Note: complete events are now handled in the onComplete callback, not here

            if (!isSupportIntent && chunk.type === 'text_chunk') {
                const text = chunk.content || '';
                if (!text) return;

                if (!streamingMessageId) {
                    streamingMessageId = `support-guidance-${Date.now()}`;
                    streamingBuffer = text;
                    setMessages(prev => [
                        ...prev,
                        {
                            id: streamingMessageId,
                            type: 'text',
                            content: text,
                            isBot: true,
                            role: 'assistant',
                            isStreaming: true,
                            metadata: { ...chunk.metadata }
                        }
                    ]);
                } else {
                    streamingBuffer += text;
                    const bufferCopy = streamingBuffer;
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === streamingMessageId
                                ? {
                                      ...msg,
                                      content: bufferCopy,
                                      metadata: { ...msg.metadata, ...chunk.metadata },
                                      isStreaming: true
                                  }
                                : msg
                        )
                    );
                }
                return;
            }

            if (!isSupportIntent && chunk.type === 'complete') {
                if (streamingMessageId) {
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === streamingMessageId
                                ? { ...msg, isStreaming: false, metadata: { ...msg.metadata, ...chunk.metadata } }
                                : msg
                        )
                    );
                    streamingMessageId = null;
                    streamingBuffer = '';
                } else if (chunk.content) {
                    setMessages(prev => [
                        ...prev,
                        {
                            id: `support-complete-${Date.now()}`,
                            type: 'text',
                            content: chunk.content,
                            isBot: true,
                            role: 'assistant',
                            metadata: { ...chunk.metadata }
                        }
                    ]);
                }
                return;
            }

            if (chunk.type === 'error') {
                toast.error(chunk.content || 'We hit a snag while processing your support request.');
                return;
            }

            if (chunk.content) {
                setMessages(prev => [
                    ...prev,
                    {
                        id: `support-${Date.now()}`,
                        type: chunk.type,
                        content: chunk.content,
                        isBot: true,
                        role: 'assistant',
                        metadata: { ...chunk.metadata }
                    }
                ]);
            }
        });

        setIsLoading(false);
        setSupportSubmitting(false);
        requestAnimationFrame(() => scrollToBottom('smooth'));
    }, [scrollToBottom]);

    const handleSupportStepSubmit = useCallback(async (supportPayload: SupportStepPayload, submission: { stepId: string; answer: any }) => {
        if (!clientId) {
            toast.error('Missing client information. Please refresh and try again.');
            return;
        }

        try {
            setSupportSubmitting(true);

            const answerText = formatSupportAnswer(supportPayload.step, submission.answer);

            setMessages(prev =>
                prev.map(msg => {
                    if (msg.type === 'support_session' && msg.supportSession?.sessionId === supportPayload.session_id) {
                        const responses = msg.supportSession.responses.filter(r => r.stepId !== submission.stepId);
                        if (answerText) {
                            responses.push({
                                stepId: submission.stepId,
                                question: supportPayload.step.title || 'Details',
                                answer: answerText
                            });
                        }
                        return {
                            ...msg,
                            supportSession: {
                                ...msg.supportSession,
                                responses,
                                currentStep: undefined,
                                awaitingNext: true
                            }
                        };
                    }
                    return msg;
                })
            );

            const response = await submitSupportStep({
                client_id: clientId,
                session_id: supportPayload.session_id,
                step_id: submission.stepId,
                answer: submission.answer,
                conversation_id: conversationId,
                guest_token: localStorage.getItem('guest_token') || undefined,
                user_id: localStorage.getItem('user_id') || undefined
            });

            processSupportChunks(response?.chunks || []);
        } catch (error) {
            console.error('Support step submission failed:', error);
            setSupportSubmitting(false);
            toast.error('Could not send those details. Please try again.');
        }
    }, [clientId, conversationId, processSupportChunks]);

    // Add auto-hide functionality for confirmation messages
    useEffect(() => {
        // Find all confirmation messages that aren't hidden
        const confirmationMessages = messages.filter(
            msg => (msg.isConfirmation || 
                   (typeof msg.content === 'string' && 
                    (msg.content.includes("Your booking has been confirmed") || 
                     msg.content.includes("Great! Your booking has been") || 
                     msg.content.includes("Your meeting has been booked")))) && 
                   !msg.hidden
        );
        
        // Set up timers to hide each confirmation after 30 seconds
        const timers = confirmationMessages.map(msg => {
            return setTimeout(() => {
                // Auto-hide this confirmation message
                setMessages(currentMessages => 
                    currentMessages.map(currentMsg => 
                        currentMsg.id === msg.id ? {...currentMsg, hidden: true} : currentMsg
                    )
                );
                console.log(`Auto-hiding confirmation message after 30 seconds: ${msg.id}`);
            }, 30000); // 30 seconds
        });
        
        // Clean up timers on unmount or when messages change
        return () => {
            timers.forEach(timer => clearTimeout(timer));
        };
    }, [messages]); // Re-run when messages change

    // Add an effect to detect system dark mode preference (after other useEffects, around line 195)
    useEffect(() => {
        // Check if system is using dark mode
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setIsDarkMode(prefersDark);
        
        // Listen for changes in color scheme preference
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            setIsDarkMode(e.matches);
        };
        
        mediaQuery.addEventListener('change', handleChange);
        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);



    // Add effect to handle chat interface reopening
    useEffect(() => {
        if (isVisible && clientId) {
            console.log('[ChatInterface] Chat interface reopened, checking messages');
            
            // Get the current messages from history
            const currentMessages = chatHistoryService.getHistory(clientId);
            console.log('[ChatInterface] Current messages:', currentMessages);
            
            // Find the last message pair (user and bot)
            const lastMessages = currentMessages.slice(-2);
            if (lastMessages.length === 2) {
                const [userMessage, botMessage] = lastMessages[0].isBot ? lastMessages.reverse() : lastMessages;
                
                console.log('[ChatInterface] Last message pair:', { 
                    userMessage, 
                    botMessage,
                    botMetadata: botMessage?.metadata,
                    botContent: botMessage?.content
                });
                
                // Skip resuming if it's a booking type message
                if (botMessage?.type === 'booking') {
                    console.log('[ChatInterface] Skipping resume for booking type message');
                    return;
                }
                
                // Check if this is a reload-interrupted message that needs resuming
                const isReloadInterrupted = botMessage?.metadata?.wasInterrupted === true;
                
                // Check if this is an active stream (close/reopen case)
                const activeStreams = chatHistoryService.getActiveStreams(clientId);
                console.log('[ChatInterface] Active streams:', activeStreams);
                
                const isActiveStream = Object.values(activeStreams).some(stream => 
                    stream.isStreaming && !stream.metadata?.wasInterrupted
                );
                
                // Check if the bot message is incomplete or errored
                const isIncompleteMessage = botMessage && (
                    !botMessage.content || 
                    botMessage.content.trim() === '' ||
                    botMessage.content === 'Could not resume message - missing original query.' ||
                    botMessage.isError
                );
                
                // Only resume if:
                // 1. Message is incomplete or was interrupted by reload
                // 2. Not currently streaming (to avoid duplicate streams)
                // 3. Has a valid user message
                // 4. Not a booking type message
                const needsResuming = botMessage && 
                    (isIncompleteMessage || isReloadInterrupted) && 
                    !isActiveStream &&
                    botMessage.type !== 'booking';  // Extra check for booking type
                
                console.log('[ChatInterface] Stream resumption checks:', {
                    isIncompleteMessage,
                    isReloadInterrupted,
                    isActiveStream,
                    needsResuming,
                    userMessageExists: !!userMessage,
                    userMessageContent: userMessage?.content,
                    botMessageId: botMessage?.id,
                    botMessageType: botMessage?.type
                });
                
                if (needsResuming && userMessage && !userMessage.isBot) {
                    console.log('[ChatInterface] Found message that needs resuming, preparing to resume stream');
                    
                    // Set loading state before resuming stream
                    setIsLoading(true);
                    
                    // Update the messages state
                    setMessages(prev => {
                        // Remove any existing versions of these messages
                        const filteredMessages = prev.filter(m => 
                            m.id !== botMessage.id && m.id !== userMessage.id
                        );
                        
                        const updatedBotMessage = {
                            ...botMessage,
                            isStreaming: true,
                            isLoading: true,
                            isError: false,
                            content: '',  // Reset content for fresh stream
                            metadata: {
                                ...botMessage.metadata,
                                originalUserQuery: userMessage.content,
                                wasInterrupted: true,
                                retryAttempted: (botMessage.metadata?.retryAttempted || 0) + 1
                            }
                        };
                        
                        console.log('[ChatInterface] Preparing to resume with messages:', {
                            userMessage,
                            updatedBotMessage
                        });
                        
                        return [
                            ...filteredMessages,
                            userMessage,
                            updatedBotMessage
                        ];
                    });
                    
                    // Resume streaming for this message
                    console.log(`[ChatInterface] Initiating stream resumption for message ${botMessage.id} with query: ${userMessage.content}`);
                    resumeStream(botMessage.id, userMessage.content);
                } else {
                    console.log('[ChatInterface] Message does not need resuming:', { 
                        needsResuming,
                        isReloadInterrupted,
                        isActiveStream,
                        isIncompleteMessage,
                        hasUserMessage: !!userMessage,
                        botMessageContent: botMessage?.content,
                        botMessageMetadata: botMessage?.metadata,
                        botMessageType: botMessage?.type
                    });
                    
                    // If there are active streams but no need for resuming, still set loading state
                    if (isActiveStream) {
                        setIsLoading(true);
                    }
                }
            } else {
                console.log('[ChatInterface] Not enough messages to check for resuming');
            }
        }
    }, [isVisible, clientId]);

    // Add focus helper function
    const focusTextarea = () => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Add keyboard shortcut for fullscreen (F11 or Ctrl+Shift+F)
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'F11' || (event.ctrlKey && event.shiftKey && event.key === 'F')) {
                event.preventDefault();
                toggleFullscreen();
            }
        };

        if (isVisible) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isVisible, isFullscreen]);

    if (!onClose) return null;

    // Helper function to convert hex to rgba
    const hexToRgba = (hex: string, opacity: number = 100): string => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    };

    // Render a dedicated, minimal Standard interface when selected (proactive uses same compact layout)
    if (theme && (theme.interfaceVariant === 'standard' || theme.interfaceVariant === 'proactive')) {
        const isProactive = theme.interfaceVariant === 'proactive';
        const proactiveAgentName = (theme.botAgentName || 'Assistant') as string;
        const proactiveAgentInitial = ((proactiveAgentName as string).trim()[0] || 'A').toUpperCase();
        // Header name: proactive uses botAgentName. The standard (sidebar) variant now
        // also honors botAgentName when set, falling back to the existing headerTitle
        // (client display name) so clients that never set it are unchanged.
        const headerTitle = isProactive
            ? proactiveAgentName
            : ((theme.botAgentName || theme.headerTitle || 'Bot') as string);
        const onlineText = (theme.headerSubtitle || 'Online') as string;
        const positionLeft = theme.interfacePosition === 'left';
        const headerBg = isProactive ? (theme.primaryColor || '#667eea') : (theme.standardHeaderBg || '#FFFFFF');
        const headerText = isProactive ? '#ffffff' : (theme.standardHeaderText || '#111827');

        return (
            <div className="std-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 2147483640 }}>
                <style>{`
                    @keyframes std-slide-in-right {
                        from { transform: translateX(100%); }
                        to { transform: translateX(0); }
                    }
                    @keyframes std-slide-in-left {
                        from { transform: translateX(-100%); }
                        to { transform: translateX(0); }
                    }

                    /* Reset host-page CSS that bleeds into the widget (text-transform: uppercase,
                       letter-spacing, italics from the host site's global rules) */
                    .std-container,
                    .std-container * {
                        text-transform: none !important;
                        letter-spacing: normal !important;
                        font-style: normal !important;
                    }

                    .std-container {
                        position: fixed;
                        ${isProactive
                            ? `bottom: 24px; ${positionLeft ? 'left: 24px' : 'right: 24px'}; top: auto; height: 600px; max-height: calc(100vh - 48px); width: 360px; max-width: calc(100vw - 32px); border-radius: 18px;`
                            : `top: 0; ${positionLeft ? 'left: 0' : 'right: 0'}; height: 100vh; width: ${isStdFullscreen ? '600px' : '380px'}; border-radius: 0;`
                        }
                        display: flex;
                        flex-direction: column;
                        background: ${isProactive ? '#f9fafb' : (theme.standardPanelBg || '#F3F4F6')};
                        box-shadow: ${isProactive ? '0 16px 40px rgba(0, 0, 0, 0.22)' : (positionLeft ? '2px 0 16px rgba(15, 23, 42, 0.12)' : '-2px 0 16px rgba(15, 23, 42, 0.12)')};
                        overflow: hidden;
                        font-family: ${theme.fontFamily || 'Inter, sans-serif'};
                        z-index: 2147483647;
                        pointer-events: auto;
                        /* Proactive: render in-place where the welcome panel
                           already lives so the swap reads as the same widget
                           changing views (no slide-from-side). */
                        animation: ${isProactive ? 'none' : `${positionLeft ? 'std-slide-in-left' : 'std-slide-in-right'} 0.32s cubic-bezier(0.4, 0, 0.2, 1)`};
                        transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    @media (max-width: 768px) {
                        .std-container {
                            inset: 0;
                            width: 100%;
                            height: 100vh;
                            border-radius: 0;
                            box-shadow: none;
                        }
                    }
                    .std-header { display: flex; align-items: center; justify-content: space-between;
                        background: ${headerBg}; padding: 16px 20px; border-bottom: 1px solid ${isProactive ? 'transparent' : (theme.standardDividerColor || '#E5E7EB')}; color: ${headerText}; }
                    .std-title { display: flex; align-items: center; gap: 10px; }
                    .std-badge { display: flex; align-items: center; gap: 6px; color: ${theme.standardHeaderMutedText || '#6B7280'}; font-size: 12px; }
                    .std-dot { width: 8px; height: 8px; border-radius: 9999px; background: #10B981; }
                    .std-menu { position: relative; }
                    .std-menu-btn { border: none; background: transparent; color: #6B7280; font-size: 18px; cursor: pointer; padding: 6px; border-radius: 8px; }
                    .std-menu-btn:hover { background: #F3F4F6; }
                    .std-menu-list { position: absolute !important; right: 0 !important; top: 32px !important; background: #ffffff !important; border: 1px solid #E5E7EB !important; border-radius: 10px !important; box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important; min-width: 160px !important; max-width: 220px !important; width: auto !important; z-index: 50 !important; overflow: hidden !important; padding: 0 !important; margin: 0 !important; }
                    /* Lock down menu-item styling with !important — host pages
                       like Secure Set ship global "button { background: red;
                       padding: 24px 40px; font-size: 20px; ... }" rules that
                       blow this up into a huge red button overlapping the
                       chat. Pin every visual property. */
                    .std-menu-item,
                    button.std-menu-item,
                    .std-menu-list button.std-menu-item {
                        display: flex !important;
                        align-items: center !important;
                        justify-content: flex-start !important;
                        gap: 8px !important;
                        width: 100% !important;
                        min-width: 0 !important;
                        max-width: none !important;
                        height: auto !important;
                        padding: 10px 12px !important;
                        margin: 0 !important;
                        background: transparent !important;
                        background-image: none !important;
                        border: none !important;
                        border-radius: 0 !important;
                        box-shadow: none !important;
                        cursor: pointer !important;
                        font-size: 14px !important;
                        font-weight: 400 !important;
                        line-height: 1.4 !important;
                        color: #374151 !important;
                        text-align: left !important;
                        text-transform: none !important;
                        letter-spacing: normal !important;
                        white-space: nowrap !important;
                        outline: none !important;
                    }
                    .std-menu-item:hover,
                    button.std-menu-item:hover,
                    .std-menu-list button.std-menu-item:hover {
                        background: #F9FAFB !important;
                        color: #374151 !important;
                    }
                    .std-menu-item span,
                    .std-menu-item svg {
                        color: inherit !important;
                        font-size: inherit !important;
                        font-weight: inherit !important;
                    }
                    .std-close { border: none; background: transparent; color: #6B7280; font-size: 16px; cursor: pointer; }
                    .std-body { flex: 1; overflow: auto; padding: 16px; }
                    .std-bubble { background: ${isProactive ? '#ffffff' : (theme.standardBotBubbleBg || '#ffffff')}; border: 1px solid ${isProactive ? '#e5e7eb' : '#E5E7EB'}; border-radius: 16px; padding: 14px 16px; color: ${isProactive ? '#1f2937' : (theme.standardBotBubbleText || '#111827')}; box-shadow: 0 2px 6px rgba(0,0,0,0.04); font-size: ${(theme.chatFontSizeResponse || theme.chatFontSizeBase || 14)}px; }
                    .std-quick-actions { display: flex; gap: 10px; padding: 10px 4px; }
                    .std-chip { padding: 8px 14px; border: 1px solid #3B82F6; color: #2563EB; background: #fff; border-radius: 9999px; font-size: 13px; cursor: pointer; }
                    .std-input { background: #ffffff; border-top: 1px solid #E5E7EB; padding: 10px 12px; display: flex; gap: 8px; font-size: ${(theme.chatFontSizeBase || 14)}px; }
                    .std-input textarea { flex: 1; resize: none; border: 1px solid #E5E7EB; border-radius: 10px; padding: 10px 12px; outline: none; font-size: ${(theme.chatFontSizePlaceholder || theme.chatFontSizeBase || 14)}px; }
                    .std-input textarea:disabled { background-color: #F3F4F6; cursor: not-allowed; opacity: 0.7; }
                    .std-send { border: none; background: ${theme.chatSendButtonBg || '#667eea'}; color: ${theme.chatSendIconColor || '#ffffff'}; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
                    .std-send:disabled { opacity: 0.5; cursor: not-allowed; }
                    .std-upload-btn { border: none; background: #6366f1; color: #ffffff; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s; }
                    .std-upload-btn:hover { background: #4f46e5; }
                    .std-upload-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                    .std-image-preview { padding: 8px 12px; background: #F9FAFB; border-bottom: 1px solid #E5E7EB; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
                    .std-image-preview-item { display: flex; align-items: center; gap: 6px; padding: 6px 10px; background: #ffffff; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 13px; color: #374151; }
                    .std-image-preview-remove { background: transparent; border: none; color: #EF4444; cursor: pointer; padding: 0 4px; font-size: 14px; font-weight: bold; }
                    .std-image-preview-remove:hover { color: #DC2626; }
                    /* Hide inline citation markers in Standard */
                    .std-container .citation-marker { display: none !important; }
                    /* Sources row */
                    .std-sources { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
                    .std-source-link { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 9999px; border: 1px solid ${theme.standardSourceBorder || '#D1D5DB'}; background: ${theme.standardSourceBg || '#ffffff'}; color: ${theme.standardSourceText || '#2563EB'}; font-size: 12px; text-decoration: none; }
                    .std-source-link:hover { background: ${theme.standardSourceHoverBg || '#F3F4F6'}; }
                    .std-inline-source { color: ${theme.standardLinkColor || '#2563EB'}; text-decoration: ${(theme.standardLinkUnderline || 'true') === 'true' ? 'underline' : 'none'}; text-underline-offset: 2px; ${((theme.standardLinkUnderline || 'true') === 'true') ? `text-decoration-thickness: ${theme.standardLinkUnderlineThickness || '1px'};` : ''} ${((theme.standardLinkUnderline || 'true') === 'true' && theme.standardLinkUnderlineColor && theme.standardLinkUnderlineColor !== '') ? `text-decoration-color: ${theme.standardLinkUnderlineColor};` : ''} font-weight: ${theme.standardLinkFontWeight || 'normal'}; }
                `}</style>

                <div className="std-container">
                    <div className="std-header">
                        <div className="std-title">
                            {isProactive ? (
                                <>
                                    <div style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.22)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        color: '#ffffff'
                                    }}>{proactiveAgentInitial}</div>
                                    <div style={{fontWeight: 600, color: headerText, fontSize: 15, letterSpacing: 0.2}}>{headerTitle}</div>
                                </>
                            ) : (
                                <>
                                    {/* Modern Bot Icon */}
                                    <BotIcon
                                        type={theme.standardBotIcon || 'ai-chip'}
                                        customImage={theme.standardBotCustomImage}
                                        size={32}
                                    />
                                    <div>
                                        <div style={{fontWeight:600, color: theme.standardHeaderText || '#111827', fontSize: (theme.chatFontSizeHeader || 16) + 'px'}}> {headerTitle} </div>
                                        <div className="std-badge"><span className="std-dot" />{onlineText}</div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            {/* Cross Button */}
                            <button
                                className="std-close-btn"
                                aria-label="Close"
                                onClick={() => onClose?.()}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: isProactive ? '#ffffff' : '#6B7280',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    padding: '6px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '32px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = isProactive ? 'rgba(255,255,255,0.18)' : '#FEE2E2';
                                    e.currentTarget.style.color = isProactive ? '#ffffff' : '#EF4444';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = isProactive ? '#ffffff' : '#6B7280';
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>

                            {/* Three-dot Menu */}
                            <div className="std-menu">
                                <button
                                    className="std-menu-btn"
                                    aria-label="Menu"
                                    onClick={() => setShowStdMenu(prev => !prev)}
                                    style={{
                                        border: 'none',
                                        background: showStdMenu ? (isProactive ? 'rgba(255,255,255,0.18)' : '#F3F4F6') : 'transparent',
                                        color: isProactive ? '#ffffff' : '#6B7280',
                                        fontSize: '20px',
                                        cursor: 'pointer',
                                        padding: '6px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        transition: 'background 0.2s ease'
                                    }}
                                    onMouseOver={(e) => !showStdMenu && (e.currentTarget.style.background = isProactive ? 'rgba(255,255,255,0.18)' : '#F3F4F6')}
                                    onMouseOut={(e) => !showStdMenu && (e.currentTarget.style.background = 'transparent')}
                                >
                                    ⋯
                                </button>
                                {showStdMenu && (
                                    <div className="std-menu-list" role="menu">
                                        {/* Proactive variant is a fixed 360x600 floating
                                            panel — fullscreen doesn't make sense there
                                            and the std-container already ignores
                                            isStdFullscreen for proactive sizing. Hide
                                            the toggle so it doesn't look broken. */}
                                        {!isProactive && (
                                            <button
                                                className="std-menu-item"
                                                role="menuitem"
                                                onClick={() => {
                                                    setShowStdMenu(false);
                                                    setIsStdFullscreen(!isStdFullscreen);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={isStdFullscreen ? faCompress : faExpandAlt} style={{fontSize: 14}} />
                                                <span>{isStdFullscreen ? 'Exit Fullscreen' : 'Fullscreen Mode'}</span>
                                            </button>
                                        )}
                                        <button
                                            className="std-menu-item"
                                            role="menuitem"
                                            onClick={() => { setShowStdMenu(false); handleClearConversation(); }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} style={{fontSize: 14}} />
                                            <span>Clear chat</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="std-body" ref={messagesContainerRef}>
                        {messages.map((message, index) => {

                            // For calendar/booking messages in standard interface - simplified view
                            if (message.type === 'booking') {
                                const calendarMessages = messages.filter(msg => msg.type === 'booking');
                                const isLatestCalendar = index === messages.findIndex(msg => 
                                    msg === calendarMessages[calendarMessages.length - 1]);
                                
                                return (
                                    <div key={index} className="std-bubble" style={{marginBottom:12}}>
                                        {/* Bot label */}
                                        <div style={{display:'flex', alignItems:'center', gap:6, color:'#6B7280', fontSize:12, marginBottom:8}}>
                                            <span style={{display:'inline-flex', width:14, height:14, borderRadius:4, background: theme.standardBotIconBg || '#E0ECFF', alignItems:'center', justifyContent:'center', color: theme.standardBotIconColor || '#2563EB', fontSize:10}}>📅</span>
                                            <span>Calendar</span>
                                        </div>
                                        
                                        {/* Calendar message text */}
                                        <div style={{marginBottom: 12, fontSize: (theme.chatFontSizeResponse || 14) + 'px', color: theme.standardBotBubbleText || '#111827'}}>
                                            {message.text || message.content}
                                        </div>
                                        
                                        {/* Show calendar or load button */}
                                        {!message.showCalendar || (!isLatestCalendar && message.initiallyHidden) ? (
                                            <div>
                                                <p style={{color:'#6B7280', fontSize:12, marginBottom:8}}>
                                                    {isLatestCalendar ? 
                                                        "Click to view available time slots." : 
                                                        "This is a previous calendar response."}
                                                </p>
                                                <button 
                                                    style={{
                                                        padding: '8px 16px',
                                                        background: '#3B82F6',
                                                        color: '#ffffff',
                                                        border: 'none',
                                                        borderRadius: 8,
                                                        fontSize: 13,
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8
                                                    }}
                                                    onClick={() => forceRefreshCalendar(index)}
                                                >
                                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                                    {isLatestCalendar ? "View Calendar" : "View This Calendar"}
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{marginTop: 12}}>
                                                {/* Compact calendar view for standard interface */}
                                                {showBookingForm && selectedEvent && selectedTime ? (
                                                    <div style={{background:'#F9FAFB', padding:12, borderRadius:8}}>
                                                        <BookingForm
                                                            eventId={selectedEvent.id}
                                                            eventTitle={selectedEvent.title}
                                                            selectedTime={selectedTime}
                                                            duration={selectedEvent.duration}
                                                            timezone={selectedEvent.timezone || userTimezone}
                                                            clientId={clientId || ''}
                                                            onSuccess={handleBookingSuccess}
                                                            onBack={() => {
                                                                setShowBookingForm(false);
                                                                setSelectedTime(null);
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div style={{background:'#F9FAFB', padding:8, borderRadius:8}}>
                                                        <CalendarView
                                                            clientId={clientId}
                                                            events={message.events || []}
                                                            timezone={message.timezone || 'UTC'}
                                                            userTimezone={message.user_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                                            onEventSelect={handleEventSelect}
                                                            onTimeSelect={(event, date, time, timeInfo) => {
                                                                setLastActiveDate(date);
                                                                setLastActiveEvent(event);
                                                                setSelectedEvent(event);
                                                                setSelectedDate(date);
                                                                setSelectedTime(timeInfo?.isoTime || time);
                                                                setShowBookingForm(true);
                                                            }}
                                                            displayInCalTimezone={displayInCalTimezone}
                                                            darkMode={false}
                                                            forceLightMode={true}
                                                            bookedSlots={bookedSlots}
                                                            compactMode={true}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            // Render user turns as right-aligned blue bubbles
                            if (!message.isBot) {
                                return (
                                    <div key={index} style={{display:'flex', justifyContent:'flex-end', marginBottom:16}}>
                                        <div>
                                            <div style={{textAlign:'right', color:'#6B7280', fontSize:12, marginRight:6, marginBottom:6}}>You</div>
                                            <div style={{
                                                background: isProactive ? (theme.primaryColor || '#2563EB') : (theme.standardUserBubbleBg || '#2563EB'),
                                                color: isProactive ? '#ffffff' : (theme.standardUserBubbleText || '#FFFFFF'),
                                                borderRadius:16, padding:'12px 16px',
                                                maxWidth:'70vw', boxShadow:'0 2px 6px rgba(0,0,0,0.08)'
                                            }}>
                                                <div style={{ fontSize: (theme.chatFontSizeUser || 14) + 'px' }}>{message.content}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            // Render bot response using StandardMessageDisplay (Grok-style inline citations)
                            let correspondingUserQuery = "";
                            for (let i = index - 1; i >= 0; i--) {
                                if (!messages[i].isBot) { correspondingUserQuery = messages[i].content; break; }
                            }

                            // Use StandardMessageDisplay component for Standard sidebar (no tabs, inline citations)
                            return (
                                <StandardMessageDisplay
                                    key={index}
                                    message={message}
                                    userQuery={correspondingUserQuery}
                                    conversationId={conversationId}
                                    clientId={clientId || undefined}
                                    onFeedback={handleFeedback}
                                    theme={theme}
                                    isWidgetMode={isWidgetMode}
                                    isAuthenticated={!!localStorage.getItem('auth_token')}
                                />
                            );
                        })}
                        {/* Optional quick actions (no Sources chip in Standard) */}
                        {Array.isArray((theme as any).quickActions) && (theme as any).quickActions.length > 0 && (
                            <div className="std-quick-actions">
                                {(theme as any).quickActions.slice(0,3).map((qa: any, i: number) => (
                                    <button key={i} className="std-chip" onClick={() => setInputMessage(qa.payload || qa.label)}>{qa.label}</button>
                                ))}
                            </div>
                        )}
                        <div ref={messagesEndRef} style={{ height: 1, width: '100%' }} />
                    </div>

                    {/* Hidden file input (Standard template) */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={currentImageStep?.accept || 'image/*'}
                        multiple={currentImageStep?.multiple || false}
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                    />

                    {/* Beautiful Input Field with inline image preview */}
                    <div style={{ padding: '12px 16px', background: '#ffffff', borderTop: '1px solid #E5E7EB' }}>
                        {/* Inline image preview chips */}
                        {uploadedImages.length > 0 && (
                            <div style={{
                                display: 'flex',
                                gap: '6px',
                                flexWrap: 'wrap',
                                marginBottom: '8px',
                                padding: '8px',
                                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                borderRadius: '12px',
                                border: '1px solid rgba(102, 126, 234, 0.2)'
                            }}>
                                {uploadedImages.map((file, index) => (
                                    <div key={index} style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '4px 8px',
                                        backgroundColor: 'rgba(102, 126, 234, 0.15)',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        color: '#374151'
                                    }}>
                                        <FontAwesomeIcon icon={faImage} style={{ fontSize: 10, color: '#667eea' }} />
                                        <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {file.name}
                                        </span>
                                        <span style={{ color: '#9CA3AF', fontSize: 10 }}>
                                            {(file.size / 1024).toFixed(0)}KB
                                        </span>
                                        <button
                                            onClick={() => handleRemoveImage(index)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#EF4444',
                                                cursor: 'pointer',
                                                padding: '0 2px',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                lineHeight: 1
                                            }}
                                            title="Remove"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Beautiful Input Field Component */}
                        <BeautifulInputField
                            ref={textareaRef}
                            value={inputMessage}
                            onChange={setInputMessage}
                            onSend={uploadedImages.length > 0 ? handleSendImages : handleSubmit}
                            placeholder={uploadedImages.length > 0 ? "Add message (optional)..." : (isProactive ? "Type your question..." : "Ask me anything...")}
                            disabled={isLoading}
                            sendButtonColor={isProactive ? (theme.primaryColor || '#667eea') : (theme.chatSendButtonBg || '#667eea')}
                            inputBgColor={isProactive ? '#f9fafb' : '#ffffff'}
                            inputTextColor="#111827"
                            borderColor={isProactive ? '#d1d5db' : '#e5e7eb'}
                            showAttachment={imageUploadEnabled}
                            onAttachmentClick={() => fileInputRef.current?.click()}
                            maxRows={4}
                        />
                    </div>

                    {/* Powered By Footer */}
                    <PoweredByFooter
                        show={!(theme.standardHideBranding === 'true' || theme.standardHideBranding === true || theme.standardHideBranding === 'True' || theme.hideBranding === true)}
                        brandColor={theme.primaryColor || '#667eea'}
                        bgColor="#f9fafb"
                        textColor="#6b7280"
                    />
                </div>
            </div>
        );
    }

    // Avoid a layout flash: while the theme is still loading we don't yet know the
    // interface variant, so falling through to this default (floating/desktop) layout
    // makes the standard/proactive "sidebar" variants briefly render as the desktop
    // modal before snapping to the sidebar. Render nothing until the theme resolves.
    // (isThemeLoading is only true in admin/preview mode; the embedded widget keeps
    // its existing behavior since it starts with isThemeLoading = false.)
    if (!theme && isThemeLoading) {
        return null;
    }

    return (
        <div className={`chat-overlay ${isFullscreen ? 'fullscreen' : ''}`}>
            {/* Default styles when no theme is set */}
            {!theme && (
                <style>
                    {`
                        /* Default chat container background */
                        .chat-container,
                        #ai-widget-container .chat-container,
                        #ai-chat-widget-container .chat-container {
                            background: linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.9) 100%) !important;
                        }
                        
                        /* Default source widget styles */
                        .source-widget-preview,
                        .source-widget,
                        .source-item {
                            background-color: #000000 !important;
                            border-color: #1e1e1e !important;
                        }
                        
                        .source-header {
                            background-color: #1e1e1e !important;
                        }
                        
                        .source-widgets-scroll {
                            background-color: #1e293b !important;
                            border-color: #475569 !important;
                        }
                    `}
                </style>
            )}
            {/* Inject dynamic theme styles */}
            {theme && (
                <style>
                    {`
                        /* Set CSS variables for theme colors */
                        :root {
                            --chat-container-gradient-start: ${hexToRgba(theme.chatContainerGradientStart || '#1e293b', Number(theme.chatContainerOpacity) || 95)};
                            --chat-container-gradient-end: ${hexToRgba(theme.chatContainerGradientEnd || '#334155', Number(theme.chatContainerOpacity) || 95)};
                            --booking-form-bg: ${hexToRgba(theme.chatContainerGradientStart || '#1e293b', 70)};
                        }
                        
                        /* Standard / Proactive variant positioning */
                        ${ (theme.interfaceVariant === 'standard' || theme.interfaceVariant === 'proactive') ? `
                        .chat-overlay .chat-container,
                        #ai-widget-container .chat-container,
                        #ai-chat-widget-container .chat-container {
                            position: fixed !important;
                            bottom: 20px !important;
                            ${ (theme.interfacePosition === 'left') ? 'left: 20px !important; right: auto !important;' : 'right: 20px !important; left: auto !important;' }
                            width: 360px !important;
                            max-height: 70vh !important;
                            border-radius: 14px !important;
                            box-shadow: 0 12px 30px rgba(0,0,0,0.15) !important;
                            overflow: hidden !important;
                        }
                        @media (max-width: 640px) {
                            .chat-overlay .chat-container,
                            #ai-widget-container .chat-container,
                            #ai-chat-widget-container .chat-container {
                                position: fixed !important;
                                inset: 0 !important;
                                width: 100% !important;
                                max-height: 100% !important;
                                border-radius: 0 !important;
                            }
                        }
                        ` : ''}
                        /* Chat container background - solid or gradient with opacity; respect interfaceVariant */
                        .chat-container,
                        #ai-widget-container .chat-container,
                        #ai-chat-widget-container .chat-container {
                            background: ${
                                (theme.interfaceVariant === 'standard' || theme.interfaceVariant === 'proactive')
                                    ? hexToRgba(theme.chatBgColor || '#ffffff', 100)
                                    : ((theme.chatContainerUseGradient === undefined || theme.chatContainerUseGradient === 'true')
                                        ? `linear-gradient(145deg, ${hexToRgba(theme.chatContainerGradientStart || '#1e293b', Number(theme.chatContainerOpacity) || 95)} 0%, ${hexToRgba(theme.chatContainerGradientEnd || '#334155', Number(theme.chatContainerOpacity) || 95)} 100%)`
                                        : hexToRgba(theme.chatContainerBg || '#374151', Number(theme.chatContainerOpacity) || 100))
                            } !important;
                        }
                        
                        /* Text colors for message content */
                        .chat-container .message-bubble,
                        .chat-container .message-bubble p,
                        .chat-container .response-content,
                        .chat-container .response-content-direct,
                        .chat-container .markdown-content,
                        .chat-container .formatted-response,
                        .chat-container .user-query-display,
                        #ai-widget-container .message-bubble,
                        #ai-widget-container .message-bubble p,
                        #ai-widget-container .response-content,
                        #ai-widget-container .response-content-direct,
                        #ai-widget-container .markdown-content,
                        #ai-widget-container .formatted-response,
                        #ai-widget-container .user-query-display,
                        #ai-chat-widget-container .message-bubble,
                        #ai-chat-widget-container .message-bubble p,
                        #ai-chat-widget-container .response-content,
                        #ai-chat-widget-container .response-content-direct,
                        #ai-chat-widget-container .markdown-content,
                        #ai-chat-widget-container .formatted-response,
                        #ai-chat-widget-container .user-query-display,
                        /* Apply to all text elements within these containers */
                        .chat-container .response-content-direct *:not(a):not(.citation-marker):not(code):not(pre),
                        .chat-container .markdown-content *:not(a):not(.citation-marker):not(code):not(pre),
                        .chat-container .formatted-response *:not(a):not(.citation-marker):not(code):not(pre),
                        #ai-widget-container .response-content-direct *:not(a):not(.citation-marker):not(code):not(pre),
                        #ai-widget-container .markdown-content *:not(a):not(.citation-marker):not(code):not(pre),
                        #ai-widget-container .formatted-response *:not(a):not(.citation-marker):not(code):not(pre),
                        #ai-chat-widget-container .response-content-direct *:not(a):not(.citation-marker):not(code):not(pre),
                        #ai-chat-widget-container .markdown-content *:not(a):not(.citation-marker):not(code):not(pre),
                        #ai-chat-widget-container .formatted-response *:not(a):not(.citation-marker):not(code):not(pre),
                        /* Specific elements that might have default colors */
                        .chat-container .markdown-content p,
                        .chat-container .markdown-content li,
                        .chat-container .markdown-content h1,
                        .chat-container .markdown-content h2,
                        .chat-container .markdown-content h3,
                        .chat-container .markdown-content h4,
                        .chat-container .markdown-content h5,
                        .chat-container .markdown-content h6,
                        .chat-container .markdown-content strong,
                        .chat-container .markdown-content em,
                        .chat-container .markdown-content span,
                        #ai-widget-container .markdown-content p,
                        #ai-widget-container .markdown-content li,
                        #ai-widget-container .markdown-content h1,
                        #ai-widget-container .markdown-content h2,
                        #ai-widget-container .markdown-content h3,
                        #ai-widget-container .markdown-content h4,
                        #ai-widget-container .markdown-content h5,
                        #ai-widget-container .markdown-content h6,
                        #ai-widget-container .markdown-content strong,
                        #ai-widget-container .markdown-content em,
                        #ai-widget-container .markdown-content span,
                        #ai-chat-widget-container .markdown-content p,
                        #ai-chat-widget-container .markdown-content li,
                        #ai-chat-widget-container .markdown-content h1,
                        #ai-chat-widget-container .markdown-content h2,
                        #ai-chat-widget-container .markdown-content h3,
                        #ai-chat-widget-container .markdown-content h4,
                        #ai-chat-widget-container .markdown-content h5,
                        #ai-chat-widget-container .markdown-content h6,
                        #ai-chat-widget-container .markdown-content strong,
                        #ai-chat-widget-container .markdown-content em,
                        #ai-chat-widget-container .markdown-content span {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override specific hardcoded colors from messageResponse.css */
                        .chat-container .formatted-response,
                        #ai-widget-container .formatted-response,
                        #ai-chat-widget-container .formatted-response {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override headers from messageRenderer.css */
                        .chat-container .markdown-content h1,
                        .chat-container .markdown-content h2,
                        .chat-container .markdown-content h3,
                        .chat-container .markdown-content h4,
                        .chat-container .markdown-content h5,
                        .chat-container .markdown-content h6,
                        .chat-container .response-content-direct h1,
                        .chat-container .response-content-direct h2,
                        .chat-container .response-content-direct h3,
                        .chat-container .response-content-direct h4,
                        .chat-container .response-content-direct h5,
                        .chat-container .response-content-direct h6,
                        #ai-widget-container .markdown-content h1,
                        #ai-widget-container .markdown-content h2,
                        #ai-widget-container .markdown-content h3,
                        #ai-widget-container .markdown-content h4,
                        #ai-widget-container .markdown-content h5,
                        #ai-widget-container .markdown-content h6,
                        #ai-widget-container .response-content-direct h1,
                        #ai-widget-container .response-content-direct h2,
                        #ai-widget-container .response-content-direct h3,
                        #ai-widget-container .response-content-direct h4,
                        #ai-widget-container .response-content-direct h5,
                        #ai-widget-container .response-content-direct h6 {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override blockquotes */
                        .chat-container .markdown-content blockquote,
                        .chat-container .response-content-direct blockquote,
                        #ai-widget-container .markdown-content blockquote,
                        #ai-widget-container .response-content-direct blockquote {
                            color: ${theme.chatTextColor} !important;
                            opacity: 0.8;
                        }
                        
                        /* Ensure user query display uses theme color */
                        .user-query-display {
                            color: ${theme.chatTextColor} !important;
                            opacity: 0.9;
                        }
                        
                        /* Source widget text in response tab - More specific selectors */
                        .source-widgets-container .source-widget-preview,
                        .source-widgets-scroll .source-widget-preview,
                        .source-widget-preview .source-content-preview,
                        .source-widget-preview .source-title-preview,
                        .message-bubble .source-widget-preview,
                        .response-content .source-widget-preview,
                        /* Very specific selectors for source title preview */
                        .response-content .source-widgets-container .source-widget-preview .source-title-preview,
                        .response-content .source-widgets-scroll .source-widget-preview .source-title-preview,
                        .tab-content .source-widgets-container .source-widget-preview .source-title-preview,
                        .tab-content .source-widgets-scroll .source-widget-preview .source-title-preview,
                        .message-response-display .source-widgets-container .source-title-preview,
                        .message-response-display .source-widgets-scroll .source-title-preview,
                        /* All text elements in source widgets */
                        .source-widgets-container .source-widget-preview *:not(a),
                        .source-widgets-scroll .source-widget-preview *:not(a),
                        .source-widget-preview *:not(a):not(.source-marker),
                        /* Catch-all for any source widget variations */
                        [class*="source-widget"] p,
                        [class*="source-widget"] span,
                        [class*="source-widget"] div:not(.source-marker),
                        /* Override any default styles with high specificity */
                        .chat-container .response-content .source-widgets-container .source-title-preview,
                        #ai-widget-container .response-content .source-widgets-container .source-title-preview,
                        #ai-chat-widget-container .response-content .source-widgets-container .source-title-preview {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Visit link in response tab should use text color, not link color */
                        .response-content .source-link-preview,
                        .tab-content[data-tab="response"] .source-link-preview,
                        .source-widgets-container .source-link-preview {
                            color: ${theme.chatTextColor} !important;
                            text-decoration: underline !important;
                        }
                        
                        /* Other source widget links in sources tab use link color */
                        .tab-content[data-tab="sources"] a,
                        .sources-list a {
                            color: ${theme.chatSourcesLinkColor || '#60a5fa'} !important;
                        }
                        
                        /* Source widget markers in response tab */
                        .response-content .source-marker,
                        .tab-content[data-tab="response"] .source-marker,
                        .source-widgets-container .source-marker,
                        .source-widgets-scroll .source-marker {
                            background-color: ${theme.chatSourcesMarkerBg || '#3b82f6'} !important;
                            color: ${theme.chatSourcesMarkerColor || '#ffffff'} !important;
                        }
                        
                        /* Source widget markers in sources tab */
                        .tab-content[data-tab="sources"] .source-marker,
                        .sources-list .source-marker,
                        .sources-content .source-marker,
                        .source-widget .source-marker-badge {
                            background-color: ${theme.chatSourcesTabMarkerBg || '#10b981'} !important;
                            color: ${theme.chatSourcesTabMarkerColor || '#ffffff'} !important;
                        }
                        
                        /* Source widget background in response tab */
                        .source-widgets-container,
                        .source-widgets-scroll {
                            background-color: ${theme.chatSourcesScrollBg || 'rgba(31, 41, 55, 0.4)'} !important;
                            border-color: ${theme.chatSourcesScrollBorderColor || 'rgba(75, 85, 99, 0.2)'} !important;
                        }
                        
                        /* Source widget in response tab uses its own background - with higher specificity */
                        .response-content .source-widget-preview,
                        .tab-content[data-tab="response"] .source-widget-preview,
                        .message-response-display .source-widget-preview,
                        .message-response-display .response-content .source-widget-preview,
                        .message-response-display .tab-content .source-widget-preview,
                        .chat-container .message-response-display .source-widget-preview,
                        #ai-widget-container .message-response-display .source-widget-preview,
                        .source-widgets-container .source-widget-preview,
                        .source-widgets-scroll .source-widget-preview {
                            background-color: ${theme.chatSourcesResponseBg || '#445062'} !important;
                            border-color: ${theme.chatSourcesItemBorderColor || 'rgba(75, 85, 99, 0.3)'} !important;
                        }
                        
                        /* Source widget in sources tab uses different background */
                        .tab-content[data-tab="sources"] .source-widget-preview,
                        .sources-list .source-widget,
                        .sources-content .source-widget {
                            background-color: ${theme.chatSourcesItemBg || 'rgba(31, 41, 55, 0.6)'} !important;
                            border-color: ${theme.chatSourcesItemBorderColor || 'rgba(75, 85, 99, 0.3)'} !important;
                        }
                        
                        /* Send button styling - with more specific selectors */
                        .send-button,
                        .chat-container .send-button,
                        #ai-widget-container .send-button,
                        #ai-chat-widget-container .send-button,
                        .chat-overlay .send-button,
                        .input-container .send-button,
                        .main-input-section .send-button,
                        button.send-button,
                        .chat-container button.send-button,
                        #ai-widget-container button.send-button,
                        #ai-chat-widget-container button.send-button {
                            color: ${theme.chatSendIconColor} !important;
                            background-color: ${theme.chatSendButtonBg} !important;
                            background: ${theme.chatSendButtonBg} !important;
                            border: none !important;
                            outline: none !important;
                        }
                        
                        /* Override any hover states */
                        .send-button:hover,
                        button.send-button:hover {
                            background-color: ${theme.chatSendButtonBg} !important;
                            background: ${theme.chatSendButtonBg} !important;
                        }
                        
                        /* Send button icon */
                        .send-button svg,
                        .chat-container .send-button svg,
                        #ai-widget-container .send-button svg,
                        #ai-chat-widget-container .send-button svg,
                        .chat-overlay .send-button svg,
                        .input-container .send-button svg,
                        .main-input-section .send-button svg,
                        button.send-button svg,
                        .send-button svg path,
                        .send-button svg * {
                            color: ${theme.chatSendIconColor} !important;
                            fill: ${theme.chatSendIconColor} !important;
                        }
                        
                        /* Close icon styling */
                        .chat-container .chat-close-icon,
                        #ai-widget-container .chat-close-icon,
                        #ai-chat-widget-container .chat-close-icon,
                        .chat-overlay .chat-close-icon {
                            color: ${theme.chatCloseIconColor} !important;
                            background-color: ${theme.chatCloseIconBg} !important;
                        }
                        
                        /* Close tooltip background */
                        .chat-container .close-tooltip,
                        #ai-widget-container .close-tooltip,
                        #ai-chat-widget-container .close-tooltip,
                        .chat-overlay .close-tooltip {
                            background-color: ${theme.chatCloseIconBg} !important;
                            color: ${theme.chatCloseIconColor} !important;
                        }
                        
                        /* Message response display background */
                        .chat-container .message-response-display,
                        #ai-widget-container .message-response-display,
                        #ai-chat-widget-container .message-response-display {
                            background-color: ${theme.chatMessageResponseBg} !important;
                        }
                        
                        /* Citation tooltip styles */
                        .citation-tooltip-portal {
                            background-color: ${theme.chatCitationTooltipBg} !important;
                            color: ${theme.chatCitationTooltipText} !important;
                        }
                        
                        .citation-tooltip-portal * {
                            color: ${theme.chatCitationTooltipText} !important;
                        }
                        
                        /* Sources tab and items customization */
                        .chat-container .source-widgets-container,
                        #ai-widget-container .source-widgets-container,
                        #ai-chat-widget-container .source-widgets-container,
                        .chat-overlay .source-widgets-container {
                            border-color: ${theme.chatSourcesTabBorderColor || '#374151'} !important;
                        }
                        
                        /* Tab buttons - non-active state */
                        .chat-container .source-widgets-container .tab-button,
                        #ai-widget-container .source-widgets-container .tab-button,
                        #ai-chat-widget-container .source-widgets-container .tab-button,
                        .chat-overlay .source-widgets-container .tab-button,
                        .tab-button,
                        .response-tabs .tab-button,
                        .message-response-display .tab-button,
                        .message-response-display .response-tabs .tab-button:not(.active) {
                            color: ${theme.chatSourcesTabTextColor || '#9ca3af'} !important;
                            border-bottom: 2px solid transparent !important;
                        }
                        
                        /* Tab button icons - non-active state */
                        .tab-button .tab-icon,
                        .tab-button svg,
                        .tab-button svg path,
                        .response-tabs .tab-button svg,
                        .response-tabs .tab-button svg path,
                        .message-response-display .tab-button svg,
                        .message-response-display .tab-button svg path,
                        .message-response-display .response-tabs .tab-button:not(.active) svg,
                        .message-response-display .response-tabs .tab-button:not(.active) svg path,
                        .tab-button:not(.active) .tab-icon,
                        .tab-button:not(.active) svg,
                        .tab-button:not(.active) svg path {
                            color: ${theme.chatSourcesTabTextColor || '#9ca3af'} !important;
                            fill: ${theme.chatSourcesTabTextColor || '#9ca3af'} !important;
                            stroke: ${theme.chatSourcesTabTextColor || '#9ca3af'} !important;
                        }
                        
                        /* Tab button hover state */
                        .tab-button:hover:not(.active),
                        .response-tabs .tab-button:hover:not(.active),
                        .message-response-display .tab-button:hover:not(.active) {
                            color: ${theme.chatSourcesTabTextColor || '#9ca3af'} !important;
                            opacity: 0.8;
                        }
                        
                        /* Tab buttons - active state */
                        .chat-container .source-widgets-container .tab-button.active,
                        #ai-widget-container .source-widgets-container .tab-button.active,
                        #ai-chat-widget-container .source-widgets-container .tab-button.active,
                        .chat-overlay .source-widgets-container .tab-button.active,
                        .tab-button.active,
                        .response-tabs .tab-button.active,
                        .message-response-display .tab-button.active,
                        .message-response-display .response-tabs .tab-button.active {
                            color: ${theme.chatSourcesTabActiveTextColor || '#3b82f6'} !important;
                            border-bottom: 2px solid ${theme.chatSourcesTabActiveTextColor || '#3b82f6'} !important;
                        }
                        
                        /* Tab button icons - active state */
                        .tab-button.active .tab-icon,
                        .tab-button.active svg,
                        .tab-button.active svg path,
                        .response-tabs .tab-button.active svg,
                        .response-tabs .tab-button.active svg path,
                        .message-response-display .tab-button.active svg,
                        .message-response-display .tab-button.active svg path,
                        .message-response-display .response-tabs .tab-button.active svg,
                        .message-response-display .response-tabs .tab-button.active svg path {
                            color: ${theme.chatSourcesTabActiveTextColor || '#3b82f6'} !important;
                            fill: ${theme.chatSourcesTabActiveTextColor || '#3b82f6'} !important;
                            stroke: ${theme.chatSourcesTabActiveTextColor || '#3b82f6'} !important;
                        }
                        
                        /* Override the :after pseudo-element */
                        .tab-button.active:after,
                        .chat-container .tab-button.active:after,
                        #ai-widget-container .tab-button.active:after,
                        #ai-chat-widget-container .tab-button.active:after,
                        .chat-overlay .tab-button.active:after {
                            background-color: ${theme.chatSourcesTabBorderColor || '#3b82f6'} !important;
                            display: none !important; /* Hide it since we're using border-bottom */
                        }
                        
                        /* Citation source items - in response and sources tab */
                        .chat-container .citation-source-item,
                        #ai-widget-container .citation-source-item,
                        #ai-chat-widget-container .citation-source-item,
                        .chat-overlay .citation-source-item,
                        .source-widget,
                        .source-item {
                            border-color: ${theme.chatSourcesItemBorderColor || '#1e1e1e'} !important;
                            background-color: ${theme.chatSourcesItemBg || '#1e1e1e'} !important;
                        }
                        
                        /* Source widget preview specific */
                        .source-widget-preview {
                            border-color: ${theme?.chatSourcesItemBorderColor || '#1e1e1e'} !important;
                            background-color: ${theme?.chatSourcesItemBg || '#000000'} !important;
                        }
                        
                        /* Force dark theme for source widgets if no theme or empty values */
                        .source-widgets-scroll .source-widget-preview,
                        .source-widgets .source-widget-preview {
                            background-color: ${(theme?.chatSourcesItemBg && theme.chatSourcesItemBg !== '') ? theme.chatSourcesItemBg : '#000000'} !important;
                            border-color: ${(theme?.chatSourcesItemBorderColor && theme.chatSourcesItemBorderColor !== '') ? theme.chatSourcesItemBorderColor : '#1e1e1e'} !important;
                        }
                        
                        .chat-container .citation-source-item .source-header,
                        #ai-widget-container .citation-source-item .source-header,
                        #ai-chat-widget-container .citation-source-item .source-header,
                        .chat-overlay .citation-source-item .source-header,
                        .source-widget .source-header,
                        .source-item .source-header,
                        .source-widgets .source-header {
                            background-color: ${theme.chatSourcesItemHeaderBg || '#1f2937'} !important;
                        }
                        
                        .chat-container .citation-source-item .source-url,
                        #ai-widget-container .citation-source-item .source-url,
                        #ai-chat-widget-container .citation-source-item .source-url,
                        .chat-overlay .citation-source-item .source-url,
                        .chat-container .citation-source-item a,
                        #ai-widget-container .citation-source-item a,
                        #ai-chat-widget-container .citation-source-item a,
                        .chat-overlay .citation-source-item a,
                        .source-widget a,
                        .source-item a,
                        .source-url,
                        /* Source widget links in responses */
                        .source-widgets-scroll a,
                        .source-widgets-container a,
                        .source-widget-preview a,
                        [class*="source-widget"] a {
                            color: ${theme.chatSourcesLinkColor || '#60a5fa'} !important;
                        }
                        
                        /* Source widgets in response */
                        .chat-container .source-widgets,
                        #ai-widget-container .source-widgets,
                        #ai-chat-widget-container .source-widgets,
                        .chat-overlay .source-widgets {
                            background-color: ${theme.chatSourcesItemBg || 'transparent'} !important;
                        }
                        
                        /* Source widget text within response messages */
                        .message-bubble .source-widgets-scroll p,
                        .message-bubble .source-widgets-scroll span,
                        .message-bubble .source-widgets-scroll div,
                        .response-content .source-widgets-scroll p,
                        .response-content .source-widgets-scroll span,
                        .response-content .source-widgets-scroll div,
                        .source-widgets-container p,
                        .source-widgets-container span,
                        .source-widgets-container div,
                        /* Comprehensive source widget text coverage */
                        [class*="source-widget"] p:not(.source-url),
                        [class*="source-widget"] span:not(.source-url),
                        [class*="source-widget"] div:not(.source-url):not(.view-source-button),
                        .source-widgets-scroll [class*="source"] *:not(a):not(button) {
                            color: ${theme.chatSourcesTextColor || '#374151'} !important;
                        }
                        
                        /* Source widgets scroll container */
                        .chat-container .source-widgets-scroll,
                        #ai-widget-container .source-widgets-scroll,
                        #ai-chat-widget-container .source-widgets-scroll,
                        .chat-overlay .source-widgets-scroll,
                        .source-widgets-scroll,
                        .source-widgets-container,
                        .response-content .source-widgets-scroll,
                        .message-bubble .source-widgets-scroll,
                        .message-response-display .source-widgets-scroll {
                            background-color: ${theme.chatSourcesScrollBg || '#f3f4f6'} !important;
                            border: 1px solid ${theme.chatSourcesScrollBorderColor || '#e5e7eb'} !important;
                            border-color: ${theme.chatSourcesScrollBorderColor || '#e5e7eb'} !important;
                        }
                        
                        /* Override existing source-widgets-container border */
                        .chat-container .source-widgets-container,
                        #ai-widget-container .source-widgets-container,
                        #ai-chat-widget-container .source-widgets-container,
                        .chat-overlay .source-widgets-container {
                            border-color: ${theme.chatSourcesScrollBorderColor || '#e5e7eb'} !important;
                            border: 1px solid ${theme.chatSourcesScrollBorderColor || '#e5e7eb'} !important;
                        }
                        
                        /* Source widget text content */
                        .chat-container .source-widget .source-content,
                        #ai-widget-container .source-widget .source-content,
                        #ai-chat-widget-container .source-widget .source-content,
                        .chat-overlay .source-widget .source-content,
                        .chat-container .source-item .source-content,
                        #ai-widget-container .source-item .source-content,
                        #ai-chat-widget-container .source-item .source-content,
                        .chat-overlay .source-item .source-content,
                        .chat-container .citation-source-item .source-content,
                        #ai-widget-container .citation-source-item .source-content,
                        #ai-chat-widget-container .citation-source-item .source-content,
                        .chat-overlay .citation-source-item .source-content,
                        .source-widgets p,
                        .source-widgets span,
                        .source-widget p,
                        .source-widget span,
                        .source-item p,
                        .source-item span,
                        /* Sources tab markers and labels */
                        .source-widget-marker,
                        .source-number,
                        .source-marker,
                        .sources-tab-content .source-number,
                        .sources-tab-content .source-marker,
                        .sources-tab-content span,
                        .sources-tab-content p,
                        .tab-content.sources-tab-content *,
                        /* Source title preview - more specific selectors */
                        .source-title-preview,
                        .chat-container .source-title-preview,
                        #ai-widget-container .source-title-preview,
                        #ai-chat-widget-container .source-title-preview,
                        .chat-overlay .source-title-preview,
                        .source-widgets .source-title-preview,
                        .source-widgets-scroll .source-title-preview,
                        .source-widget-preview .source-title-preview,
                        .response-content .source-title-preview,
                        .tab-content .source-title-preview,
                        /* Source marker badge */
                        .source-marker-badge,
                        .chat-container .source-marker-badge,
                        #ai-widget-container .source-marker-badge,
                        #ai-chat-widget-container .source-marker-badge,
                        .chat-overlay .source-marker-badge,
                        /* Citation markers in sources tab */
                        .citation-marker,
                        .sources-tab-content .citation-marker,
                        .tab-content.sources-tab-content .citation-marker,
                        .source-citation-marker,
                        .sources-section .citation-marker,
                        .sources-section .source-marker,
                        /* All source text elements */
                        .source-text,
                        .source-widget-content,
                        .source-widget-preview .source-content,
                        .source-widget-preview p,
                        .source-widget-preview span,
                        .source-widget-preview .source-widget-text,
                        .source-widget-preview .source-title-preview,
                        .source-widget-preview .source-excerpt,
                        .source-widgets-scroll .source-widget-preview,
                        .source-widgets-scroll .source-widget-preview *:not(a),
                        .source-widgets-container .source-widget-preview *:not(a),
                        .message-response-display .source-widgets-scroll *:not(a),
                        .response-content .source-widgets-scroll *:not(a),
                        .citation-source-item p,
                        .citation-source-item span,
                        .citation-source-item .source-text,
                        .source-item .source-text,
                        /* Additional selectors for response tab sources */
                        .message-response-display .source-title-preview,
                        .message-bubble .source-title-preview,
                        .response-tab .source-title-preview,
                        .source-widgets-container .source-title-preview,
                        /* Very specific selectors for response tab source widgets */
                        .response-content .source-widgets-container .source-widget-preview .source-content-preview .source-title-preview,
                        .tab-content .response-content .source-widgets-container .source-widget-preview .source-title-preview,
                        .message-response-display .response-content .source-widgets-container .source-title-preview,
                        /* Source widgets in messages */
                        .message-bubble .source-widget-preview p,
                        .message-bubble .source-widget-preview span,
                        .message-bubble .source-widget-preview .source-widget-text,
                        .response-content .source-widget-preview p,
                        .response-content .source-widget-preview span,
                        .response-content .source-widget-preview .source-widget-text,
                        /* Ensure highest specificity for source title preview */
                        body .chat-overlay .chat-container .response-content .source-widgets-container .source-title-preview,
                        body #ai-widget-container .response-content .source-widgets-container .source-title-preview,
                        body #ai-chat-widget-container .response-content .source-widgets-container .source-title-preview {
                            color: ${theme.chatSourcesTextColor || '#374151'} !important;
                        }
                        
                        /* Override default badge background */
                        .source-marker-badge {
                            background-color: transparent !important;
                        }
                        
                        /* Sources tab ONLY - text color customization */
                        .sources-tab-content *:not(a):not(button),
                        .sources-tab-content .source-widget *:not(a):not(button),
                        .sources-tab-content .source-widget-preview *:not(a):not(button),
                        .sources-tab-content .citation-source-item *:not(a):not(button),
                        .sources-tab-content .source-content *:not(a):not(button),
                        .sources-tab-content .source-title *:not(a):not(button),
                        .sources-tab-content .source-text *:not(a):not(button),
                        .sources-tab-content .source-description *:not(a):not(button),
                        .sources-tab-content .source-excerpt *:not(a):not(button),
                        .sources-tab-content .source-excerpt,
                        .tab-content.sources-tab-content *:not(a):not(button),
                        .tab-content.sources-tab-content .source-widget *:not(a):not(button),
                        .tab-content.sources-tab-content .source-widget-preview *:not(a):not(button),
                        .tab-content.sources-tab-content .citation-source-item *:not(a):not(button),
                        .tab-content.sources-tab-content .source-content *:not(a):not(button),
                        .tab-content.sources-tab-content .source-title *:not(a):not(button),
                        .tab-content.sources-tab-content .source-text *:not(a):not(button),
                        .tab-content.sources-tab-content .source-description *:not(a):not(button),
                        .tab-content.sources-tab-content .source-excerpt *:not(a):not(button),
                        .tab-content.sources-tab-content .source-excerpt,
                        .sources-tab-content p:not(.source-url),
                        .sources-tab-content span:not(.source-url),
                        .sources-tab-content div:not(.source-url):not(.view-source-button),
                        .tab-content.sources-tab-content p:not(.source-url),
                        .tab-content.sources-tab-content span:not(.source-url),
                        .tab-content.sources-tab-content div:not(.source-url):not(.view-source-button) {
                            color: ${theme.chatSourcesTextColor || '#374151'} !important;
                        }
                        
                        /* Extra specific targeting for source-excerpt in sources tab */
                        .sources-tab-content .source-excerpt,
                        .tab-content.sources-tab-content .source-excerpt,
                        .sources-section .source-excerpt,
                        .chat-container .sources-tab-content .source-excerpt,
                        #ai-widget-container .sources-tab-content .source-excerpt,
                        #ai-chat-widget-container .sources-tab-content .source-excerpt,
                        .chat-overlay .sources-tab-content .source-excerpt,
                        .chat-container .tab-content.sources-tab-content .source-excerpt,
                        #ai-widget-container .tab-content.sources-tab-content .source-excerpt,
                        #ai-chat-widget-container .tab-content.sources-tab-content .source-excerpt,
                        .chat-overlay .tab-content.sources-tab-content .source-excerpt {
                            color: ${theme.chatSourcesTextColor || '#374151'} !important;
                        }
                        
                        /* Citation marker styling in response - perfect square */
                        .response-content .citation-marker,
                        .message-bubble .citation-marker,
                        .message-response-display .citation-marker,
                        .citation-number:not(.sources-tab-content *),
                        .source-number:not(.sources-tab-content *),
                        .source-marker-badge:not(.sources-tab-content *) {
                            background-color: ${theme.chatSourcesMarkerBg || '#3b82f6'} !important;
                            color: ${theme.chatSourcesMarkerColor || '#ffffff'} !important;                   
                            height: 24px !important;
                            padding: 5px !important;
                            border-radius: 0 !important;
                            font-weight: 600 !important;
                            display: inline-flex !important;
                            align-items: center !important;
                            justify-content: center !important;
                            line-height: 1 !important;
                            font-size: 12px !important;
                            text-align: center !important;
                            vertical-align: middle !important;
                        }
                        
                        /* Citation marker styling in sources tab - perfect square with different color */
                        .sources-tab-content .citation-marker,
                        .tab-content.sources-tab-content .citation-marker,
                        .sources-section .citation-marker,
                        .sources-section .source-marker,
                        .sources-tab-content .citation-number,
                        .sources-tab-content .source-number,
                        .sources-tab-content .source-marker-badge {
                            background-color: ${theme.chatSourcesTabMarkerBg || '#10b981'} !important;
                            color: ${theme.chatSourcesTabMarkerColor || '#ffffff'} !important;
                            width: 20px !important;
                            height: 20px !important;
                            padding: 0 !important;
                            border-radius: 0 !important;
                            font-weight: 600 !important;
                            display: inline-flex !important;
                            align-items: center !important;
                            justify-content: center !important;
                            line-height: 1 !important;
                            font-size: 12px !important;
                            text-align: center !important;
                            vertical-align: middle !important;
                        }
                        
                        /* Hide source footer meta */
                        .source-footer-meta,
                        .chat-container .source-footer-meta,
                        #ai-widget-container .source-footer-meta,
                        #ai-chat-widget-container .source-footer-meta,
                        .chat-overlay .source-footer-meta {
                            display: none !important;
                        }
                        
                        /* Response and sources tab text */
                        .chat-container .response-content,
                        #ai-widget-container .response-content,
                        #ai-chat-widget-container .response-content,
                        .chat-overlay .response-content,
                        .chat-container .tab-content,
                        #ai-widget-container .tab-content,
                        #ai-chat-widget-container .tab-content,
                        .chat-overlay .tab-content {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override messageRenderer.css hardcoded colors */
                        .chat-container .response-content-direct,
                        #ai-widget-container .response-content-direct,
                        #ai-chat-widget-container .response-content-direct,
                        .chat-overlay .response-content-direct {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override formatted-response hardcoded color #e5e7eb */
                        .chat-container .formatted-response,
                        #ai-widget-container .formatted-response,
                        #ai-chat-widget-container .formatted-response,
                        .chat-overlay .formatted-response,
                        .message-response-display .formatted-response {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override formatted-response heading colors */
                        .chat-container .formatted-response h1,
                        .chat-container .formatted-response h2,
                        .chat-container .formatted-response h3,
                        .chat-container .formatted-response h4,
                        .chat-container .formatted-response h5,
                        .chat-container .formatted-response h6,
                        .chat-container .formatted-response strong,
                        #ai-widget-container .formatted-response h1,
                        #ai-widget-container .formatted-response h2,
                        #ai-widget-container .formatted-response h3,
                        #ai-widget-container .formatted-response h4,
                        #ai-widget-container .formatted-response h5,
                        #ai-widget-container .formatted-response h6,
                        #ai-widget-container .formatted-response strong,
                        #ai-chat-widget-container .formatted-response h1,
                        #ai-chat-widget-container .formatted-response h2,
                        #ai-chat-widget-container .formatted-response h3,
                        #ai-chat-widget-container .formatted-response h4,
                        #ai-chat-widget-container .formatted-response h5,
                        #ai-chat-widget-container .formatted-response h6,
                        #ai-chat-widget-container .formatted-response strong,
                        .chat-overlay .formatted-response h1,
                        .chat-overlay .formatted-response h2,
                        .chat-overlay .formatted-response h3,
                        .chat-overlay .formatted-response h4,
                        .chat-overlay .formatted-response h5,
                        .chat-overlay .formatted-response h6,
                        .chat-overlay .formatted-response strong,
                        .message-response-display .formatted-response h1,
                        .message-response-display .formatted-response h2,
                        .message-response-display .formatted-response h3,
                        .message-response-display .formatted-response h4,
                        .message-response-display .formatted-response h5,
                        .message-response-display .formatted-response h6,
                        .message-response-display .formatted-response strong {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override formatted-response additional text elements */
                        .chat-container .formatted-response em,
                        .chat-container .formatted-response blockquote,
                        .chat-container .formatted-response p,
                        .chat-container .formatted-response li,
                        .chat-container .formatted-response span,
                        .chat-container .formatted-response div:not(.citation-marker),
                        #ai-widget-container .formatted-response em,
                        #ai-widget-container .formatted-response blockquote,
                        #ai-widget-container .formatted-response p,
                        #ai-widget-container .formatted-response li,
                        #ai-widget-container .formatted-response span,
                        #ai-widget-container .formatted-response div:not(.citation-marker),
                        #ai-chat-widget-container .formatted-response em,
                        #ai-chat-widget-container .formatted-response blockquote,
                        #ai-chat-widget-container .formatted-response p,
                        #ai-chat-widget-container .formatted-response li,
                        #ai-chat-widget-container .formatted-response span,
                        #ai-chat-widget-container .formatted-response div:not(.citation-marker),
                        .chat-overlay .formatted-response em,
                        .chat-overlay .formatted-response blockquote,
                        .chat-overlay .formatted-response p,
                        .chat-overlay .formatted-response li,
                        .chat-overlay .formatted-response span,
                        .chat-overlay .formatted-response div:not(.citation-marker),
                        .message-response-display .formatted-response em,
                        .message-response-display .formatted-response blockquote,
                        .message-response-display .formatted-response p,
                        .message-response-display .formatted-response li,
                        .message-response-display .formatted-response span,
                        .message-response-display .formatted-response div:not(.citation-marker) {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override markdown content hardcoded colors */
                        .chat-container .markdown-content p,
                        .chat-container .markdown-content li,
                        .chat-container .markdown-content strong,
                        #ai-widget-container .markdown-content p,
                        #ai-widget-container .markdown-content li,
                        #ai-widget-container .markdown-content strong,
                        #ai-chat-widget-container .markdown-content p,
                        #ai-chat-widget-container .markdown-content li,
                        #ai-chat-widget-container .markdown-content strong,
                        .chat-overlay .markdown-content p,
                        .chat-overlay .markdown-content li,
                        .chat-overlay .markdown-content strong {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Ensure all message bubble content uses theme color */
                        .message-bubble,
                        .message-bubble *:not(a):not(.citation-marker):not(code):not(pre):not(.hljs *),
                        .message-response-display,
                        .message-response-display *:not(a):not(.citation-marker):not(code):not(pre):not(.hljs *) {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Override any remaining text elements in response */
                        .response-tab-content,
                        .response-tab-content *:not(a):not(.citation-marker):not(code):not(pre):not(.hljs *),
                        .tab-content .response-content,
                        .tab-content .response-content *:not(a):not(.citation-marker):not(code):not(pre):not(.hljs *),
                        .formatted-response ul,
                        .formatted-response ol,
                        .formatted-response table,
                        .formatted-response td,
                        .formatted-response th {
                            color: ${theme.chatTextColor} !important;
                        }
                        
                        /* Hyperlinks in message responses */
                        .chat-container .message-content a,
                        .chat-container .response-content a,
                        .chat-container .markdown-content a,
                        .chat-container .formatted-response a,
                        #ai-widget-container .message-content a,
                        #ai-widget-container .response-content a,
                        #ai-widget-container .markdown-content a,
                        #ai-widget-container .formatted-response a,
                        #ai-chat-widget-container .message-content a,
                        #ai-chat-widget-container .response-content a,
                        #ai-chat-widget-container .markdown-content a,
                        #ai-chat-widget-container .formatted-response a,
                        .chat-overlay .message-content a,
                        .chat-overlay .response-content a,
                        .chat-overlay .markdown-content a,
                        .chat-overlay .formatted-response a,
                        .message-bubble a,
                        .prose a {
                            color: ${theme.chatHyperlinkColor || '#60a5fa'} !important;
                        }
                        
                        .chat-container .message-content a:hover,
                        .chat-container .response-content a:hover,
                        .chat-container .markdown-content a:hover,
                        .chat-container .formatted-response a:hover,
                        #ai-widget-container .message-content a:hover,
                        #ai-widget-container .response-content a:hover,
                        #ai-widget-container .markdown-content a:hover,
                        #ai-widget-container .formatted-response a:hover,
                        #ai-chat-widget-container .message-content a:hover,
                        #ai-chat-widget-container .response-content a:hover,
                        #ai-chat-widget-container .markdown-content a:hover,
                        #ai-chat-widget-container .formatted-response a:hover,
                        .chat-overlay .message-content a:hover,
                        .chat-overlay .response-content a:hover,
                        .chat-overlay .markdown-content a:hover,
                        .chat-overlay .formatted-response a:hover,
                        .message-bubble a:hover,
                        .prose a:hover {
                            color: ${theme.chatHyperlinkColor || '#93bbfc'} !important;
                            text-decoration: underline !important;
                        }
                        
                        /* Feedback icons - comprehensive selectors */
                        .chat-container .feedback-buttons button,
                        #ai-widget-container .feedback-buttons button,
                        #ai-chat-widget-container .feedback-buttons button,
                        .chat-overlay .feedback-buttons button,
                        .ChatBubble_button,
                        .feedback-button,
                        .feedback-btn,
                        .feedback-icon,
                        .reaction-button,
                        .ChatBubble_buttonGroup button,
                        .ChatBubble_buttonGroup .ChatBubble_button,
                        button[aria-label*="thumbs"],
                        button[aria-label*="feedback"],
                        .message-feedback button,
                        .message-actions button,
                        /* Additional selectors for feedback buttons */
                        .feedback-buttons .feedback-btn,
                        .message-bubble .feedback-btn,
                        .response-content .feedback-btn {
                            color: ${theme.chatFeedbackIconColor || '#9ca3af'} !important;
                            background-color: ${theme.chatFeedbackBg || 'transparent'} !important;
                            border: 1px solid ${theme.chatFeedbackBorderColor || 'transparent'} !important;
                            border-radius: 6px !important;
                        }
                        
                        /* Feedback icon SVGs and emojis */
                        .feedback-buttons button svg,
                        .ChatBubble_button svg,
                        .feedback-button svg,
                        .feedback-btn svg,
                        .feedback-icon svg,
                        .reaction-button svg,
                        .ChatBubble_buttonGroup button svg,
                        .message-feedback button svg,
                        .message-actions button svg,
                        .feedback-buttons button svg path,
                        .ChatBubble_button svg path,
                        .feedback-btn svg path {
                            color: ${theme.chatFeedbackIconColor || '#9ca3af'} !important;
                            fill: ${theme.chatFeedbackIconColor || '#9ca3af'} !important;
                        }
                        
                        /* For emoji feedback buttons */
                        .feedback-btn:not(.active) {
                            filter: grayscale(0.5) opacity(0.8);
                        }
                        
                        /* Ensure feedback button text/icon colors */
                        .feedback-btn,
                        .feedback-buttons .feedback-btn,
                        button.feedback-btn {
                            color: ${theme.chatFeedbackIconColor || '#9ca3af'} !important;
                        }
                        
                        /* Override default feedback button styles */
                        .feedback-buttons .feedback-btn {
                            border: 2px solid ${theme.chatFeedbackBorderColor || '#e5e7eb'} !important;
                            background: ${theme.chatFeedbackBg || '#ffffff'} !important;
                        }
                        
                        .feedback-buttons .feedback-btn:hover:not(:disabled) {
                            border-color: ${theme.chatFeedbackHoverBorderColor || '#3b82f6'} !important;
                            background: ${theme.chatFeedbackHoverBg || '#f3f4f6'} !important;
                        }
                        
                        /* Active state for specific feedback types */
                        .feedback-buttons .feedback-btn.thumbs-up.active {
                            border-color: ${theme.chatFeedbackActiveBorderColor || '#10b981'} !important;
                            background: ${theme.chatFeedbackActiveBg || '#d1fae5'} !important;
                        }
                        
                        .feedback-buttons .feedback-btn.thumbs-down.active {
                            border-color: ${theme.chatFeedbackActiveBorderColor || '#ef4444'} !important;
                            background: ${theme.chatFeedbackActiveBg || '#fee2e2'} !important;
                        }
                        
                        .feedback-buttons .feedback-btn.need-review.active {
                            border-color: ${theme.chatFeedbackActiveBorderColor || '#f59e0b'} !important;
                            background: ${theme.chatFeedbackActiveBg || '#fef3c7'} !important;
                        }
                        
                        /* Hover states */
                        .chat-container .feedback-buttons button:hover,
                        #ai-widget-container .feedback-buttons button:hover,
                        #ai-chat-widget-container .feedback-buttons button:hover,
                        .chat-overlay .feedback-buttons button:hover,
                        .ChatBubble_button:hover,
                        .feedback-button:hover,
                        .feedback-btn:hover,
                        .feedback-icon:hover,
                        .reaction-button:hover,
                        .ChatBubble_buttonGroup button:hover,
                        button[aria-label*="thumbs"]:hover,
                        button[aria-label*="feedback"]:hover,
                        .message-feedback button:hover,
                        .message-actions button:hover,
                        .feedback-buttons .feedback-btn:hover:not(:disabled),
                        .message-bubble .feedback-btn:hover:not(:disabled),
                        .response-content .feedback-btn:hover:not(:disabled) {
                            color: ${theme.chatFeedbackIconHoverColor || '#6b7280'} !important;
                            background-color: ${theme.chatFeedbackHoverBg || 'rgba(107, 114, 128, 0.1)'} !important;
                            border-color: ${theme.chatFeedbackHoverBorderColor || '#e5e7eb'} !important;
                            filter: none !important;
                        }
                        
                        /* Hover state SVGs */
                        .feedback-buttons button:hover svg,
                        .ChatBubble_button:hover svg,
                        .feedback-button:hover svg,
                        .feedback-icon:hover svg,
                        .reaction-button:hover svg,
                        .ChatBubble_buttonGroup button:hover svg,
                        .message-feedback button:hover svg,
                        .message-actions button:hover svg,
                        .feedback-buttons button:hover svg path,
                        .ChatBubble_button:hover svg path {
                            color: ${theme.chatFeedbackIconHoverColor || '#6b7280'} !important;
                            fill: ${theme.chatFeedbackIconHoverColor || '#6b7280'} !important;
                        }
                        
                        /* Active/selected states */
                        .chat-container .feedback-buttons button.active,
                        #ai-widget-container .feedback-buttons button.active,
                        #ai-chat-widget-container .feedback-buttons button.active,
                        .chat-overlay .feedback-buttons button.active,
                        .ChatBubble_button.active,
                        .feedback-button.active,
                        .feedback-btn.active,
                        .feedback-icon.active,
                        .reaction-button.active,
                        .ChatBubble_buttonGroup button.active,
                        .feedback-buttons button[data-active="true"],
                        .feedback-buttons button[aria-pressed="true"],
                        button[aria-label*="thumbs"].active,
                        button[aria-label*="feedback"].active,
                        .message-feedback button.active,
                        .message-actions button.active,
                        .feedback-buttons .feedback-btn.active,
                        .message-bubble .feedback-btn.active,
                        .response-content .feedback-btn.active {
                            color: ${theme.chatFeedbackActiveColor || '#3b82f6'} !important;
                            background-color: ${theme.chatFeedbackActiveBg || 'rgba(59, 130, 246, 0.1)'} !important;
                            border-color: ${theme.chatFeedbackActiveBorderColor || '#3b82f6'} !important;
                            filter: none !important;
                        }
                        
                        /* Active state SVGs */
                        .feedback-buttons button.active svg,
                        .ChatBubble_button.active svg,
                        .feedback-button.active svg,
                        .feedback-icon.active svg,
                        .reaction-button.active svg,
                        .ChatBubble_buttonGroup button.active svg,
                        .message-feedback button.active svg,
                        .message-actions button.active svg,
                        .feedback-buttons button.active svg path,
                        .ChatBubble_button.active svg path,
                        .feedback-buttons button[data-active="true"] svg,
                        .feedback-buttons button[data-active="true"] svg path {
                            color: ${theme.chatFeedbackActiveColor || '#3b82f6'} !important;
                            fill: ${theme.chatFeedbackActiveColor || '#3b82f6'} !important;
                        }
                        
                        /* View Source button customization */
                        .view-source-button,
                        .chat-container .view-source-button,
                        #ai-widget-container .view-source-button,
                        #ai-chat-widget-container .view-source-button,
                        .chat-overlay .view-source-button {
                            background-color: ${theme.chatViewSourceBg || 'rgba(59, 130, 246, 0.1)'} !important;
                            color: ${theme.chatViewSourceColor || '#60a5fa'} !important;
                            text-decoration: none !important;
                        }
                        
                        .view-source-button:hover,
                        .chat-container .view-source-button:hover,
                        #ai-widget-container .view-source-button:hover,
                        #ai-chat-widget-container .view-source-button:hover,
                        .chat-overlay .view-source-button:hover {
                            background-color: ${theme.chatViewSourceHoverBg || 'rgba(59, 130, 246, 0.2)'} !important;
                            color: ${theme.chatViewSourceHoverColor || '#93c5fd'} !important;
                            text-decoration: none !important;
                        }
                        
                        /* Feedback Tag Styles */
                        .feedback-tag,
                        .chat-container .feedback-tag,
                        #ai-widget-container .feedback-tag,
                        #ai-chat-widget-container .feedback-tag,
                        .chat-overlay .feedback-tag {
                            background: ${theme.chatFeedbackTagBg || '#364356'} !important;
                            border: 1px solid ${theme.chatFeedbackTagBorderColor || '#475569'} !important;
                            color: ${theme.chatFeedbackTagColor || theme.chatTextColor || '#ffffff'} !important;
                        }
                        
                        .feedback-tag:hover,
                        .chat-container .feedback-tag:hover,
                        #ai-widget-container .feedback-tag:hover,
                        #ai-chat-widget-container .feedback-tag:hover,
                        .chat-overlay .feedback-tag:hover {
                            background: ${theme.chatFeedbackTagHoverBg || '#4a5568'} !important;
                            border-color: ${theme.chatFeedbackTagHoverBorderColor || '#718096'} !important;
                        }
                        
                        .feedback-tag:active,
                        .chat-container .feedback-tag:active,
                        #ai-widget-container .feedback-tag:active,
                        #ai-chat-widget-container .feedback-tag:active,
                        .chat-overlay .feedback-tag:active {
                            background: ${theme.chatFeedbackTagActiveBg || '#2d3748'} !important;
                        }
                        
                        /* Feedback Tags Label */
                        .feedback-tags-label,
                        .chat-container .feedback-tags-label,
                        #ai-widget-container .feedback-tags-label,
                        #ai-chat-widget-container .feedback-tags-label,
                        .chat-overlay .feedback-tags-label {
                            color: ${theme.chatFeedbackTagLabelColor || theme.chatTextColor || '#ffffff'} !important;
                        }
                        
                        /* Feedback Thank You Message */
                        .feedback-thank-you,
                        .chat-container .feedback-thank-you,
                        #ai-widget-container .feedback-thank-you,
                        #ai-chat-widget-container .feedback-thank-you,
                        .chat-overlay .feedback-thank-you {
                            color: ${theme.chatFeedbackThankYouColor || theme.chatTextColor || '#ffffff'} !important;
                        }
                        
                        /* Final override for source widget text in response tab - highest specificity */
                        body .response-content .source-widgets-container .source-widget-preview .source-title-preview,
                        body .tab-content .response-content .source-widgets-container .source-widget-preview .source-title-preview,
                        body .message-response-display .response-content .source-widgets-container .source-widget-preview .source-title-preview,
                        body .chat-overlay .response-content .source-widgets-container .source-widget-preview .source-title-preview,
                        body #ai-widget-container .response-content .source-widgets-container .source-widget-preview .source-title-preview,
                        body #ai-chat-widget-container .response-content .source-widgets-container .source-widget-preview .source-title-preview,
                        /* Override ChatInterface.css hardcoded colors */
                        body .source-title-preview {
                            color: ${theme.chatSourcesTextColor || theme.chatTextColor || '#e5e7eb'} !important;
                        }
                        
                        /* Source excerpt text */
                        body .response-content .source-widgets-container .source-widget-preview .source-excerpt-preview,
                        body .source-excerpt-preview {
                            color: ${theme.chatSourcesTextColor || theme.chatTextColor || '#e5e7eb'} !important;
                            opacity: 0.8;
                        }
                        
                        /* Typography System - Proper Font Sizes */
                        /* Base font size for chat container - sets the foundation */
                        body .chat-overlay .chat-container,
                        body #ai-widget-container .chat-container,
                        body #ai-chat-widget-container .chat-container,
                        body .chat-container {
                            font-size: ${theme?.chatFontSizeBase ? `${theme.chatFontSizeBase}px` : '14px'} !important;
                            font-family: ${theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'} !important;
                            line-height: 1.6 !important;
                        }
                        
                        /* Response message typography - optimized for readability */
                        body .chat-overlay .message-response-display,
                        body .chat-overlay .message-wrapper.bot-response,
                        body .chat-overlay .response-content,
                        body .chat-overlay .response-content-direct,
                        body .chat-overlay .markdown-content,
                        body .chat-overlay .formatted-response,
                        body #ai-widget-container .message-response-display,
                        body #ai-widget-container .response-content,
                        body #ai-widget-container .markdown-content,
                        body #ai-chat-widget-container .message-response-display,
                        body #ai-chat-widget-container .response-content,
                        body #ai-chat-widget-container .markdown-content {
                            font-size: ${theme?.chatFontSizeResponse ? `${theme.chatFontSizeResponse}px` : '14px'} !important;
                        }
                        
                        /* Response paragraphs and text */
                        body .chat-overlay .response-content p,
                        body .chat-overlay .markdown-content p,
                        body .chat-overlay .formatted-response p,
                        body #ai-widget-container .response-content p,
                        body #ai-widget-container .markdown-content p,
                        body #ai-chat-widget-container .response-content p,
                        body #ai-chat-widget-container .markdown-content p {
                            font-size: ${theme?.chatFontSizeResponse ? `${theme.chatFontSizeResponse}px` : '14px'} !important;
                            margin: 0.8em 0;
                        }
                        
                        /* Response headings - relative sizing for proper hierarchy */
                        body .chat-overlay .response-content h1,
                        body .chat-overlay .markdown-content h1,
                        body #ai-widget-container .response-content h1,
                        body #ai-widget-container .markdown-content h1 {
                            font-size: 1.8em !important;
                            font-weight: 600 !important;
                            margin-top: 1.5em;
                            margin-bottom: 0.5em;
                        }
                        
                        body .chat-overlay .response-content h2,
                        body .chat-overlay .markdown-content h2,
                        body #ai-widget-container .response-content h2,
                        body #ai-widget-container .markdown-content h2 {
                            font-size: 1.5em !important;
                            font-weight: 600 !important;
                            margin-top: 1.5em;
                            margin-bottom: 0.5em;
                        }
                        
                        body .chat-overlay .response-content h3,
                        body .chat-overlay .markdown-content h3,
                        body #ai-widget-container .response-content h3,
                        body #ai-widget-container .markdown-content h3 {
                            font-size: 1.2em !important;
                            font-weight: 600 !important;
                            margin-top: 1.2em;
                            margin-bottom: 0.5em;
                        }
                        
                        body .chat-overlay .response-content h4,
                        body .chat-overlay .markdown-content h4,
                        body #ai-widget-container .response-content h4,
                        body #ai-widget-container .markdown-content h4 {
                            font-size: 1.1em !important;
                            font-weight: 600 !important;
                            margin-top: 1em;
                            margin-bottom: 0.5em;
                        }
                        
                        /* Lists */
                        body .chat-overlay .response-content ul,
                        body .chat-overlay .response-content ol,
                        body .chat-overlay .markdown-content ul,
                        body .chat-overlay .markdown-content ol {
                            font-size: ${theme?.chatFontSizeResponse ? `${theme.chatFontSizeResponse}px` : '14px'} !important;
                            margin: 0.5em 0;
                            padding-left: 1.5em;
                        }
                        
                        body .chat-overlay .response-content li,
                        body .chat-overlay .markdown-content li {
                            margin: 0.3em 0;
                        }
                        
                        /* User message typography */
                        body .chat-overlay .message-wrapper.user,
                        body .chat-overlay .user-input-bubble,
                        body .chat-overlay .user-message,
                        body .chat-overlay .user-query-display,
                        body #ai-widget-container .message-wrapper.user,
                        body #ai-chat-widget-container .message-wrapper.user,
                        body #ai-widget-container .user-query-display,
                        body #ai-chat-widget-container .user-query-display {
                            font-size: ${theme?.chatFontSizeUser ? `${theme.chatFontSizeUser}px` : '14px'} !important;
                        }
                        
                        /* Chat header typography */
                        body .chat-overlay .chat-header,
                        body #ai-widget-container .chat-header,
                        body #ai-chat-widget-container .chat-header {
                            font-size: ${theme?.chatFontSizeHeader ? `${theme.chatFontSizeHeader}px` : '16px'} !important;
                            font-weight: 600 !important;
                        }
                        
                        /* Input and placeholder typography */
                        body .chat-overlay input,
                        body .chat-overlay textarea,
                        body #ai-widget-container input,
                        body #ai-widget-container textarea,
                        body #ai-chat-widget-container input,
                        body #ai-chat-widget-container textarea {
                            font-size: ${theme?.chatFontSizePlaceholder ? `${theme.chatFontSizePlaceholder}px` : '14px'} !important;
                            font-family: ${theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'} !important;
                        }
                        
                        body .chat-overlay input::placeholder,
                        body .chat-overlay textarea::placeholder,
                        body #ai-widget-container input::placeholder,
                        body #ai-widget-container textarea::placeholder,
                        body #ai-chat-widget-container input::placeholder,
                        body #ai-chat-widget-container textarea::placeholder {
                            font-size: ${theme?.chatFontSizePlaceholder ? `${theme.chatFontSizePlaceholder}px` : '14px'} !important;
                            opacity: 0.7;
                        }
                        
                        /* Link Customization */
                        .chat-container a,
                        .chat-container .message-response-display a,
                        .chat-container .message-content a,
                        #ai-widget-container a,
                        #ai-chat-widget-container a {
                            text-decoration: ${theme.chatLinkUnderline === 'true' ? 'underline' : 'none'} !important;
                            color: ${theme.chatHyperlinkColor || '#3b82f6'} !important;
                            font-weight: ${theme.chatLinkFontWeight || 'normal'} !important;
                        }
                        
                        /* Link hover effects */
                        .chat-container a:hover,
                        .chat-container .message-response-display a:hover,
                        #ai-widget-container a:hover,
                        #ai-chat-widget-container a:hover {
                            text-decoration: underline !important;
                            opacity: 0.8;
                        }

                        /* Override dark mode specific styles */
                        @media (prefers-color-scheme: dark) {
                            body .source-title-preview,
                            body .source-excerpt-preview {
                                color: ${theme.chatSourcesTextColor || theme.chatTextColor || '#e5e7eb'} !important;
                            }
                        }
                    `}
                </style>
            )}
            <div 
                className={`chat-container ${isFullscreen ? 'fullscreen' : ''} ${isSidebarOpen ? 'sidebar-open' : ''}`}
            >
                <div className="chat-handle" onClick={onClose} title="Close chat" />
                
                {/* Mobile Menu Toggle (1440px and below) */}
                <div className="mobile-menu-toggle" onClick={toggleSidebar}>
                    <FontAwesomeIcon icon={faBars} />
                </div>

                {/* Control buttons */}
                <div className="chat-controls">
                    {/* Clear Conversation Button */}
                    {theme?.chatClearButtonShow === 'true' && theme?.chatClearButtonPosition === 'header-right' && messages.some(msg => msg.isBot === false) && (
                        <div 
                            className="chat-clear-icon" 
                            onClick={handleClearConversation} 
                            title={theme?.chatClearButtonText || "Clear Chat"}
                            role="button"
                            aria-label={theme?.chatClearButtonText || "Clear Chat"}
                            style={{
                                backgroundColor: theme?.chatClearButtonBg || '#374151',
                                color: theme?.chatClearButtonColor || '#ffffff',
                                padding: '8px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                marginRight: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = theme?.chatClearButtonHoverBg || '#4b5563';
                                e.currentTarget.style.color = theme?.chatClearButtonHoverColor || '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = theme?.chatClearButtonBg || '#374151';
                                e.currentTarget.style.color = theme?.chatClearButtonColor || '#ffffff';
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </div>
                    )}
                    
                    {/* Fullscreen Toggle Button - Hidden for now */}
                    {/* <div 
                        className="chat-fullscreen-icon" 
                        onClick={toggleFullscreen} 
                        title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                        role="button"
                        aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                    >
                        <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                    </div> */}
                    
                    {/* Close Button */}
                    <div 
                        className="chat-close-icon" 
                        onClick={onClose} 
                        role="button"
                        aria-label="Close chat"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </div>
                </div>
                
                {/* Sidebar Overlay for mobile */}
                {isSidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}

                {/* Input Section (Sidebar on mobile) */}
                <div className={`main-input-section ${isSidebarOpen ? 'sidebar-open' : ''}`}>
                    <div className="input-container">
                        {/* Textarea with inline image preview */}
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                        }}>
                            {/* Inline image preview chips inside textarea area */}
                            {uploadedImages.length > 0 && (
                                <div style={{
                                    display: 'flex',
                                    gap: '8px',
                                    flexWrap: 'wrap',
                                    marginBottom: '8px',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    border: '1px solid rgba(99, 102, 241, 0.3)'
                                }}>
                                    {uploadedImages.map((file, index) => (
                                        <div key={index} style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '4px 8px',
                                            backgroundColor: 'rgba(99, 102, 241, 0.2)',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            color: '#E2E8F0'
                                        }}>
                                            <FontAwesomeIcon icon={faImage} style={{ fontSize: 12, color: '#A5B4FC' }} />
                                            <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {file.name}
                                            </span>
                                            <span style={{ color: '#94A3B8', fontSize: 11 }}>
                                                {(file.size / 1024).toFixed(0)}KB
                                            </span>
                                            <button
                                                onClick={() => handleRemoveImage(index)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#F87171',
                                                    cursor: 'pointer',
                                                    padding: '0 4px',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    lineHeight: 1
                                                }}
                                                title="Remove image"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <textarea
                                ref={textareaRef}
                                value={inputMessage}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder={uploadedImages.length > 0 ? "Add a message (optional)..." : "Type your message..."}
                                className="message-input"
                                disabled={isLoading}
                                style={{
                                    height: uploadedImages.length > 0 ? 'calc(100% - 60px)' : 'calc(100% - 50px)'
                                }}
                            />
                        </div>

                        {/* Hidden file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={currentImageStep?.accept || 'image/*'}
                            multiple={currentImageStep?.multiple || false}
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                        />

                        {/* Bottom row: Upload icon + Send button */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '8px'
                        }}>
                            {/* Image Upload Icon - only show when enabled */}
                            {imageUploadEnabled && (
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="image-upload-icon"
                                    title={currentImageStep?.description || "Upload image"}
                                    disabled={isLoading}
                                    style={{
                                        backgroundColor: theme?.chatSendButtonBg || '#667eea',
                                        color: theme?.chatSendIconColor || '#ffffff',
                                        border: 'none',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        minWidth: '48px',
                                        minHeight: '48px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: isLoading ? 0.5 : 1
                                    }}
                                >
                                    <FontAwesomeIcon icon={faImage} style={{ width: 18, height: 18 }} />
                                </button>
                            )}

                            <button
                                onClick={uploadedImages.length > 0 ? handleSendImages : handleSubmit}
                                className="send-button"
                                title={uploadedImages.length > 0 ? `Send ${uploadedImages.length} image(s)` : "Send message"}
                                disabled={isLoading || (uploadedImages.length === 0 && !inputMessage.trim())}
                                style={{
                                    opacity: (isLoading || (uploadedImages.length === 0 && !inputMessage.trim())) ? 0.5 : 1,
                                    backgroundColor: theme?.chatSendButtonBg || '#667eea',
                                    color: theme?.chatSendIconColor || '#ffffff',
                                    border: 'none'
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faPaperPlane}
                                    style={{
                                        color: theme?.chatSendIconColor || '#ffffff'
                                    }}
                                />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Messages Section */}
                <div className="messages-section">
                    <div className="messages-container" ref={messagesContainerRef}>
                        {messages.map((message, index) => {
                            // Add data-message-index to the outer div of each message for lookup
                            const messageKey = message.id || index;
                            const outerDivProps = { 'data-message-index': index };

                            if (message.type === 'booking') {
                                // Find if this is the most recent calendar message
                                const calendarMessages = messages.filter(msg => msg.type === 'booking');
                                const isLatestCalendar = index === messages.findIndex(msg => 
                                    msg === calendarMessages[calendarMessages.length - 1]);
                                
                                // For history calendar messages that aren't the latest or not showing
                                if (!message.showCalendar || (!isLatestCalendar && message.initiallyHidden)) {
                                    return (
                                        <div key={messageKey} className={`message-wrapper bot calendar-wrapper`} {...outerDivProps}>
                                            
                                            <div className={`message-bubble calendar-placeholder`}>
                                                <p>{message.text || message.content}</p>
                                                <p className="text-gray-500 text-sm mt-2">
                                                    {isLatestCalendar ? 
                                                        "This is the most recent calendar response." : 
                                                        "This is a previous calendar response. For better performance, only the most recent calendar is automatically displayed."}
                                                </p>
                                                <button 
                                                    className="load-calendar-button"
                                                    onClick={() => forceRefreshCalendar(index)}
                                                >
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                                                    {isLatestCalendar ? "Load Calendar" : "View This Calendar"}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }
                                
                                // Render Calendar Message with full calendar component
                                return (
                                    <div key={messageKey} className="message-wrapper bot-response" {...outerDivProps}>
                                        <div className="message-content">
                                            {/* Display the calendar text message */}
                                            <div className="calendar-text-message">
                                                {message.text || message.content}
                                            </div>
                                            
                                            {/* Conditionally show booking form or calendar view */}
                                            <div className="calendar-component-wrapper">
                                                {showBookingForm && selectedEvent && selectedTime ? (
                                                    <div className="booking-form-container">
                                                        <div className="booking-form-card">
                                                            <BookingForm
                                                                eventId={selectedEvent.id}
                                                                eventTitle={selectedEvent.title}
                                                                selectedTime={selectedTime}
                                                                duration={selectedEvent.duration}
                                                                timezone={selectedEvent.timezone || userTimezone}
                                                                clientId={clientId || ''}
                                                                onSuccess={handleBookingSuccess}
                                                                onBack={() => {
                                                                    setShowBookingForm(false);
                                                                    setSelectedTime(null);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <CalendarView
                                                        clientId={clientId}
                                                        events={message.events || []}
                                                        timezone={message.timezone || 'UTC'}
                                                        userTimezone={message.user_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                                        onEventSelect={handleEventSelect}
                                                        onTimeSelect={(event, date, time, timeInfo) => {
                                                            console.log('Time selected in ChatInterface:', { event, date, time, timeInfo });
                                                            
                                                            // Store the last active selection for potential refreshes
                                                            setLastActiveDate(date);
                                                            setLastActiveEvent(event);
                                                            
                                                            // Show booking form
                                                            setSelectedEvent(event);
                                                            setSelectedDate(date);
                                                            setSelectedTime(timeInfo?.isoTime || time);
                                                            setShowBookingForm(true);
                                                        }}
                                                        displayInCalTimezone={displayInCalTimezone}
                                                        darkMode={isDarkMode}
                                                        forceLightMode={true}
                                                        bookedSlots={bookedSlots}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            } else if (!message.isBot) {
                                // Skip rendering user messages separately - they'll be shown in MessageResponseDisplay
                                return null;
                            } else if (message.type === 'support_session' && message.supportSession) {
                                return (
                                    <div key={messageKey} {...outerDivProps}>
                                        <SupportSessionMessage
                                            session={message.supportSession}
                                            submitting={supportSubmitting && activeSupportSession === message.supportSession.sessionId}
                                            onSubmitStep={handleSupportStepSubmit}
                                            variant="default"
                                        />
                                    </div>
                                );
                            } else {
                                // Render Bot Response using MessageResponseDisplay (keeping existing structure)
                                let correspondingUserQuery = "";
                                for (let i = index - 1; i >= 0; i--) {
                                    if (!messages[i].isBot) {
                                        correspondingUserQuery = messages[i].content;
                                        break;
                                    }
                                }
                                return (
                                    <MessageResponseDisplay
                                        key={messageKey}
                                        message={message}
                                        userQuery={correspondingUserQuery}
                                        onFeedback={handleFeedback}
                                        {...outerDivProps}
                                    />
                                );
                            }
                        })}
                        {/* ... loading placeholder ... */}
                        <div ref={messagesEndRef} style={{ height: "1px", width: "100%" }} />
                    </div>
                </div>
            </div>

            {/* Mobile-optimized citation system */}
            <CitationDrawer
                visible={citationDrawer.visible}
                citations={citationDrawer.citations}
                selectedCitationIndex={citationDrawer.selectedIndex}
                onClose={closeCitationDrawer}
                onCitationSelect={handleCitationSelect}
                mode={citationDrawer.mode}
                anchorElement={citationDrawer.anchorElement}
            />
            
            {/* Desktop fallback citation tooltip */}
            {!isMobile && (
                <CitationTooltip
                    visible={citationTooltip.visible && !citationDrawer.visible}
                    title={citationTooltip.title}
                    url={citationTooltip.url}
                    content={citationTooltip.content}
                    x={citationTooltip.x}
                    y={citationTooltip.y}
                    position={citationTooltip.position}
                    horizontalPosition={citationTooltip.horizontalPosition}
                    sourceType={citationTooltip.sourceType}
                    theme="dark"
                    backgroundColor={theme?.chatCitationTooltipBg}
                    textColor={theme?.chatCitationTooltipText}
                    onMouseEnter={tooltipEventHandlers.onMouseEnter}
                    onMouseLeave={tooltipEventHandlers.onMouseLeave}
                />
            )}
        </div>
    );
};

export default ChatInterface;
